import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
    MessageSquare, Clock, CheckCircle, XCircle, Send, ArrowLeftRight, Pause,
    Inbox, Trash2, Mic, Paperclip, Zap, ExternalLink, ArrowLeft, CheckCheck,
    Loader2, Search, X, PlusCircle, PhoneIncoming, PhoneMissed, Ban,
    ChevronDown, Reply, Forward, Trash, Copy, FileText, UserPlus, Pencil,
    User, Edit3, Save, AlertTriangle, Contact, Square, Eye,
    Volume2, VolumeX
} from 'lucide-react';

import api from '../services/api';
import { socket } from '../services/socket';
import Modal from '../components/Modal';
import LinkifiedText from '../components/LinkifiedText';
import ImageEditor from '../components/ImageEditor';
import ClientFormModal from '../components/ClientFormModal';

// ==================================================================================
// 1. CONFIGURAÇÕES E UTILITÁRIOS
// ==================================================================================

const BASE_MEDIA_URL = 'https://wadesk.com.br';

// 🚨 FUNÇÃO DE LIMPEZA VISUAL (MOSTRA SÓ NÚMEROS)
const cleanPhoneDisplay = (p) => {
    if (!p) return '';
    return p.replace('@s.whatsapp.net', '').replace('@lid', '').replace(/[^0-9]/g, '');
};

// 🚨 FUNÇÃO PARA ENCURTAR NOME DA EMPRESA
const formatCompanyName = (companyName) => {
    if (!companyName) return '';
    if (companyName.length > 20) {
        return companyName.substring(0, 20) + '...';
    }
    return companyName;
};

const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
};

const cleanPhone = (p) => p ? p.replace(/\D/g, '').replace('@s.whatsapp.net', '') : '';

// 🚨 COMPONENTES VISUAIS AUXILIARES
const ContactMessage = ({ message }) => {
    const lines = message.body.split('\n');
    const name = lines[0]?.replace('Contato: ', '') || 'Contato';
    const phone = lines[1]?.replace('Tel: ', '') || '';

    return (
        <div className="bg-gray-100 border border-gray-300 rounded-lg p-3 flex items-center gap-3 w-64 my-1">
            <div className="bg-blue-100 p-2 rounded-full text-blue-600">
                <Contact className="w-6 h-6" />
            </div>
            <div className="flex-1 overflow-hidden">
                <p className="font-bold text-gray-800 text-sm truncate">{name}</p>
                <p className="text-xs text-gray-500">{phone}</p>
            </div>
            {phone && (
                <button
                    onClick={() => { navigator.clipboard.writeText(phone); alert('Número copiado!'); }}
                    className="text-blue-600 text-xs font-bold hover:bg-blue-50 p-1 rounded"
                >
                    Copiar
                </button>
            )}
        </div>
    );
};

const CallLogMessage = ({ message }) => {
    const isMissed = message.body?.includes('PERDIDA') || message.body?.includes('RECUSADA');
    const isAnswered = message.body?.includes('ATENDIDA');

    let iconColor = 'text-blue-600';
    let bgColor = 'bg-blue-100';
    let Icon = PhoneIncoming;

    if (isMissed) {
        iconColor = 'text-red-600';
        bgColor = 'bg-red-100';
        Icon = PhoneMissed;
    } else if (isAnswered) {
        iconColor = 'text-green-600';
        bgColor = 'bg-green-100';
        Icon = PhoneIncoming;
    }

    return (
        <div className="flex justify-center my-4 w-full select-none">
            <div className={`bg-gray-100 border border-gray-300 shadow-sm rounded-lg px-4 py-2 flex items-center gap-3 max-w-xs`}>
                <div className={`p-2 rounded-full ${bgColor} ${iconColor}`}>
                    <Icon className="w-5 h-5" />
                </div>
                <div className="flex flex-col">
                    <span className="text-sm font-bold text-gray-700">{message.body || 'Chamada'}</span>
                    <span className="text-[10px] text-gray-500 uppercase font-semibold">{new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
            </div>
        </div>
    );
};

const MessageHoverMenu = ({ message, onReply, onForward, onDelete, onReact, onEdit, onClose }) => {
    const isCall = message.type === 'call_log' || message.body?.includes('Chamada');
    const canEdit = message.fromMe && (!message.type || message.type === 'text') && !message.deleted;
    const commonEmojis = ['👍', '❤️', '😂', '😮', '😢', '🙏'];
    const doAction = (fn) => { fn(); onClose(); };
    return (
        <>
            {/* Backdrop invisível - fecha o menu ao clicar fora */}
            <div className="fixed inset-0 z-[49]" onClick={onClose} />
            <div className="absolute top-0 right-0 z-[50]">
                {/* Barra de emojis rápidos */}
                {!isCall && (
                    <div className="absolute -top-11 right-0 bg-white rounded-full shadow-lg border border-gray-200 flex items-center gap-0.5 px-1.5 py-1" style={{ whiteSpace: 'nowrap' }}>
                        {commonEmojis.map((emoji) => (
                            <button key={emoji} type="button" onClick={() => doAction(() => onReact(message, emoji))} className="w-8 h-8 flex items-center justify-center hover:scale-125 transition-all duration-150 text-xl rounded-full hover:bg-gray-100 cursor-pointer">{emoji}</button>
                        ))}
                        <button type="button" onClick={() => doAction(() => onReact(message, ''))} className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded-full ml-0.5 cursor-pointer" title="Remover reação"><Ban className="w-3.5 h-3.5 text-gray-400" /></button>
                    </div>
                )}
                {/* Dropdown de ações */}
                <div className="absolute top-6 right-0 bg-white rounded-md shadow-xl border border-gray-100 min-w-[200px] flex flex-col overflow-hidden py-1">
                    {!isCall && (
                        <>
                            <button type="button" onClick={() => doAction(() => onReply(message))} className="w-full px-6 py-2.5 text-left text-[13px] hover:bg-[#f5f6f6] flex items-center gap-3 text-[#3b4a54] cursor-pointer"><Reply className="w-4 h-4 text-[#8696a0]" /> Responder</button>
                            <button type="button" onClick={() => doAction(() => onForward(message))} className="w-full px-6 py-2.5 text-left text-[13px] hover:bg-[#f5f6f6] flex items-center gap-3 text-[#3b4a54] cursor-pointer"><Forward className="w-4 h-4 text-[#8696a0]" /> Encaminhar</button>
                            {canEdit && <button type="button" onClick={() => doAction(() => onEdit(message))} className="w-full px-6 py-2.5 text-left text-[13px] hover:bg-[#f5f6f6] flex items-center gap-3 text-[#3b4a54] cursor-pointer"><Pencil className="w-4 h-4 text-[#8696a0]" /> Editar</button>}
                            {message.body && <button type="button" onClick={() => doAction(() => navigator.clipboard.writeText(message.body))} className="w-full px-6 py-2.5 text-left text-[13px] hover:bg-[#f5f6f6] flex items-center gap-3 text-[#3b4a54] cursor-pointer"><Copy className="w-4 h-4 text-[#8696a0]" /> Copiar</button>}
                        </>
                    )}
                    {message.fromMe ? (
                        <>
                            <button type="button" onClick={() => doAction(() => onDelete(message, 'me'))} className="w-full px-6 py-2.5 text-left text-[13px] hover:bg-[#f5f6f6] flex items-center gap-3 text-[#e53935] cursor-pointer"><Trash className="w-4 h-4" /> Apagar p/ mim</button>
                            <button type="button" onClick={() => doAction(() => onDelete(message, 'everyone'))} className="w-full px-6 py-2.5 text-left text-[13px] hover:bg-[#f5f6f6] flex items-center gap-3 text-[#e53935] cursor-pointer"><Trash className="w-4 h-4" /> Apagar p/ todos</button>
                        </>
                    ) : (
                        <button type="button" onClick={() => doAction(() => onDelete(message, 'me'))} className="w-full px-6 py-2.5 text-left text-[13px] hover:bg-[#f5f6f6] flex items-center gap-3 text-[#e53935] cursor-pointer"><Trash className="w-4 h-4" /> Apagar mensagem</button>
                    )}
                </div>
            </div>
        </>
    );
};

const MessageReadStatus = ({ message, chatType }) => {
    if (!message.fromMe) return null;
    const isGroup = chatType === 'group' || chatType === 'broadcast';
    if (message.status === 'read' && !isGroup) return <CheckCheck className="w-3 h-3 text-blue-500" />;
    return <CheckCheck className="w-3 h-3 text-gray-400" />;
};

const MessageReaction = ({ reaction, fromMe }) => {
    if (!reaction || !reaction.emoji) return null;
    return (<div className={`absolute -bottom-3 ${fromMe ? '-left-2' : '-right-2'} bg-white border border-gray-200 rounded-full px-1.5 py-0.5 shadow-sm z-10 text-xs flex items-center justify-center min-w-[24px]`}><span className="text-sm leading-none">{reaction.emoji}</span></div>);
};

// Componente para exibir ligações do WhatsApp
const CallMessage = ({ message }) => {
    const isVideo = message.callType === 'video' || message.body?.includes('vídeo');
    const isMissed = message.callOutcome !== 'ACCEPTED' || message.body?.includes('não atendida');
    const duration = message.duration || 0;

    let durationText = '';
    if (duration > 0) {
        const mins = Math.floor(duration / 60);
        const secs = duration % 60;
        durationText = ` (${mins}:${secs.toString().padStart(2, '0')})`;
    }

    return (
        <div className="flex items-center gap-2 p-3 bg-blue-50/50 rounded-lg">
            {isMissed ? (
                <PhoneMissed className="w-5 h-5 text-red-600" />
            ) : (
                <PhoneIncoming className="w-5 h-5 text-blue-600" />
            )}
            <div>
                <p className={`text-sm font-bold ${isMissed ? 'text-red-900' : 'text-blue-900'}`}>
                    {isVideo ? '📹 Chamada de vídeo' : '📞 Chamada de voz'}
                </p>
                <p className="text-xs text-gray-600">
                    {isMissed ? 'Não atendida' : `Atendida${durationText}`}
                </p>
            </div>
        </div>
    );
};

// Componente para indicar mensagem apagada - estilo WhatsApp
const DeletedMessage = ({ fromMe }) => (
    <div className="flex items-center gap-1.5 py-0.5">
        <Ban className="w-3.5 h-3.5 text-gray-400 shrink-0" />
        <span className="text-[13px] text-gray-500 italic">
            {fromMe ? 'Você apagou esta mensagem' : 'Esta mensagem foi apagada'}
        </span>
    </div>
);
// Indicador pequeno para mensagens do cliente apagadas (mantém conteúdo visível)
const DeletedIndicator = () => (
    <div className="flex items-center gap-1 mt-1 opacity-70">
        <Ban className="w-3 h-3 text-red-400" />
        <span className="text-[11px] text-red-400 italic">O cliente apagou esta mensagem</span>
    </div>
);

// ====================================================================================
// 3. COMPONENTE PRINCIPAL
// ====================================================================================
const ChatScreen = ({ currentUser, clients, externalCompanies, companyData, isMuted, setIsMuted, ticketToOpen, clearTicketToOpen }) => {

    // --- ESTADOS ---
    const [chats, setChats] = useState([]);
    const [messages, setMessages] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const selectedChatRef = useRef(null); // Ref para acesso nos listeners de socket

    // --- CACHE DE MENSAGENS (evita requisição HTTP a cada clique) ---
    const messagesCache = useRef({}); // { chatId: messages[] }
    const [loadingMessages, setLoadingMessages] = useState(false);

    // --- PAGINAÇÃO DE MENSAGENS ---
    const MSG_PAGE_SIZE = 100;
    const [msgPage, setMsgPage] = useState(1);

    // Mantém as refs sincronizadas
    const [filterStatus, setFilterStatus] = useState('open');
    const filterStatusRef = useRef('open'); // Ref para evitar stale closure no socket handler
    useEffect(() => {
        selectedChatRef.current = selectedChat;
    }, [selectedChat]);

    useEffect(() => {
        filterStatusRef.current = filterStatus;
    }, [filterStatus]);

    const [clientsList, setClientsList] = useState([]);
    const [usersList, setUsersList] = useState([]);
    const [quickMessages, setQuickMessages] = useState([]);

    // UI
    const [inputText, setInputText] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const searchDebounceRef = useRef(null);
    const [debouncedSearch, setDebouncedSearch] = useState('');
    // Pega a data local correta sem sofrer desvio de UTC (YYY-MM-DD no fuso do usuário)
    const getTodayLocal = () => {
        const _d = new Date();
        return `${_d.getFullYear()}-${String(_d.getMonth() + 1).padStart(2, '0')}-${String(_d.getDate()).padStart(2, '0')}`;
    };
    const [dateFilter, setDateFilter] = useState({ start: getTodayLocal(), end: getTodayLocal() });
    const [counts, setCounts] = useState({ waiting: 0, unreadActive: 0, external: 0, paused: 0 });

    // Controle
    const [replyingTo, setReplyingTo] = useState(null);
    const [editingMessage, setEditingMessage] = useState(null);
    const [hoveredMessage, setHoveredMessage] = useState(null);
    const [activeMenuMessageId, setActiveMenuMessageId] = useState(null);
    const [isSending, setIsSending] = useState(false);
    const [isGlobalLoading, setIsGlobalLoading] = useState(false);

    // Mídia
    const [viewImage, setViewImage] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [imageToEdit, setImageToEdit] = useState(null);
    const [showImageEditor, setShowImageEditor] = useState(false);

    // Áudio
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [recordedAudio, setRecordedAudio] = useState(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const recordingIntervalRef = useRef(null);

    // Modais
    const [isStartChatModalOpen, setIsStartChatModalOpen] = useState(false);
    const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);
    const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
    const [isQuickMsgModalOpen, setIsQuickMsgModalOpen] = useState(false);
    const [forwardModal, setForwardModal] = useState({ isOpen: false, message: null });
    const [actionModal, setActionModal] = useState({ isOpen: false, type: null });
    const [pauseReason, setPauseReason] = useState('');
    const [osModal, setOsModal] = useState({ isOpen: false, data: null });
    const [addAttendantModal, setAddAttendantModal] = useState({ isOpen: false });
    const [historyModal, setHistoryModal] = useState({ isOpen: false, data: [] });
    const [loadingHistory, setLoadingHistory] = useState(false);

    // NOVO: Estado para detalhes do histórico
    const [historyDetails, setHistoryDetails] = useState({ isOpen: false, ticket: null, messages: [] });
    const [loadingDetails, setLoadingDetails] = useState(false);

    // 🆕 NOVO: Estado para modal de categorização
    const [categoryModal, setCategoryModal] = useState({ isOpen: false, actionType: null }); // actionType: 'finish' | 'cancel'
    const [categoriesList, setCategoriesList] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState('');
    const [selectedSubcategoryId, setSelectedSubcategoryId] = useState('');

    // NOVO: Função para abrir detalhes
    const handleOpenHistoryDetails = async (ticket) => {
        setHistoryDetails({ isOpen: true, ticket, messages: [] });
        setLoadingDetails(true);
        try {
            const res = await api.get(`/chats/${ticket._id || ticket.id}/messages`);
            setHistoryDetails(prev => ({ ...prev, messages: Array.isArray(res.data) ? res.data : [] }));
        } catch (e) {
            console.error(e);
            alert('Erro ao carregar mensagens.');
        } finally {
            setLoadingDetails(false);
        }
    };

    // Modal de Cliente
    const [isClientModalOpen, setIsClientModalOpen] = useState(false);
    const [clientFormModal, setClientFormModal] = useState({ isOpen: false, clientId: null });
    const [pendingAction, setPendingAction] = useState(null);
    const [clientFormData, setClientFormData] = useState({ name: '', phone: '', company: '' });

    // Inputs Modais
    const [transferTarget, setTransferTarget] = useState('');
    const [newChatClient, setNewChatClient] = useState('');
    const [newChatMessage, setNewChatMessage] = useState('');
    const [clientSearchTerm, setClientSearchTerm] = useState(''); // NOVO: Busca de cliente
    const [selectedAttendants, setSelectedAttendants] = useState([]);

    // Refs
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);

    // --- HELPERS DE FILTRO E STATUS ---
    const getStatusBadge = (status) => {
        const badges = { waiting: { text: 'Pendente', class: 'bg-yellow-100 text-yellow-700', icon: Clock }, open: { text: 'Aberto', class: 'bg-green-100 text-green-700', icon: MessageSquare }, active: { text: 'Aberto', class: 'bg-green-100 text-green-700', icon: MessageSquare }, paused: { text: 'Pausado', class: 'bg-orange-100 text-orange-700', icon: Pause }, external: { text: 'Externo', class: 'bg-purple-100 text-purple-700', icon: ExternalLink }, ended: { text: 'Finalizado', class: 'bg-gray-100 text-gray-700', icon: CheckCircle }, finished: { text: 'Finalizado', class: 'bg-gray-100 text-gray-700', icon: CheckCircle }, canceled: { text: 'Cancelado', class: 'bg-red-100 text-red-700', icon: XCircle } };
        const badge = badges[status] || badges.waiting; const Icon = badge.icon;
        return <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold ${badge.class}`}><Icon className="w-3 h-3" /> {badge.text}</span>;
    };

    // Debounce no campo de busca para evitar re-render a cada tecla
    const handleSearchChange = useCallback((value) => {
        setSearchTerm(value);
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            setDebouncedSearch(value);
        }, 200);
    }, []);

    const filteredChats = useMemo(() => {
        if (!Array.isArray(chats)) return [];
        return chats.filter(chat => {
            let statusMatch = false;

            // Filtro por status
            if (filterStatus === 'waiting') {
                // ALTERAÇÃO: 'waiting_evaluation' removido para esconder chamados em avaliação da aba Pendentes
                statusMatch = ['waiting'].includes(chat.status);
            } else if (filterStatus === 'open') {
                // Conversas abertas: só mostra se o agente for o responsável OU estiver nos atendentes
                const isOpen = ['open', 'active'].includes(chat.status);
                const currentUserId = String(currentUser?._id || currentUser?.id || '');
                // agentId pode vir como string, ObjectId, ou objeto populado {_id, name}
                const agentIdStr = chat.agentId
                    ? String(chat.agentId?._id || chat.agentId)
                    : '';
                const isMine = agentIdStr === currentUserId ||
                    (chat.attendants && chat.attendants.some(a => {
                        const aIdStr = String(a?._id || a);
                        return aIdStr === currentUserId;
                    }));
                statusMatch = isOpen && isMine;
            } else if (filterStatus === 'paused') {
                statusMatch = chat.status === 'paused';
            } else if (filterStatus === 'external') {
                statusMatch = chat.status === 'external';
            } else if (filterStatus === 'canceled') {
                statusMatch = chat.status === 'canceled';
            } else if (filterStatus === 'finished') {
                statusMatch = ['ended', 'finished'].includes(chat.status);
            } else {
                statusMatch = true;
            }

            // Filtro de busca com debounce
            const searchMatch = !debouncedSearch ||
                chat.name?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
                cleanPhone(chat.contact_id).includes(debouncedSearch);

            return statusMatch && searchMatch;
        });
    }, [chats, filterStatus, debouncedSearch, currentUser]);

    // --- EFEITOS ---
    useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

    useEffect(() => {
        loadChats(); loadClients(); loadUsers(); loadQuickMessages(); loadCategories();
        socket.on('new_message', handleSocketNewMessage);
        socket.on('chat_updated', handleSocketChatUpdate);
        socket.on('new_ticket_created', handleSocketNewTicket);
        // ✅ CORRIGIDO: listener duplicado removido
        socket.on('message-status-update', handleMessageStatusUpdate);
        socket.on('message-reaction', handleMessageReaction);
        socket.on('message_updated', handleSocketMessageUpdate);
        socket.on('message_deleted', handleMessageDeleted);
        socket.on('message_edited', handleMessageEdited);
        // ✅ Atualiza foto de perfil dos clientes em tempo real
        const handleClientUpdated = (data) => {
            if (!data || !data.clientId || !data.profilePicUrl) return;
            setChats(prev => prev.map(c =>
                String(c.clientId) === String(data.clientId)
                    ? { ...c, profilePicUrl: data.profilePicUrl }
                    : c
            ));
        };
        socket.on('client_updated', handleClientUpdated);
        return () => {
            socket.off('new_message', handleSocketNewMessage);
            socket.off('chat_updated', handleSocketChatUpdate);
            socket.off('new_ticket_created', handleSocketNewTicket);
            socket.off('message-status-update', handleMessageStatusUpdate);
            socket.off('message-reaction', handleMessageReaction);
            socket.off('message_updated', handleSocketMessageUpdate);
            socket.off('message_deleted', handleMessageDeleted);
            socket.off('message_edited', handleMessageEdited);
            socket.off('client_updated', handleClientUpdated);
        };
    }, []); // ✅ Sockets registrados UMA VEZ - não reconectar a cada mudança

    useEffect(() => {
        // Só limpa a seleção se o chat selecionado não for compatível com o novo filtro
        if (selectedChat) {
            const isCompatible = (filterStatus === 'open' && ['open', 'active'].includes(selectedChat.status)) ||
                (filterStatus === selectedChat.status);
            if (!isCompatible) setSelectedChat(null);
        } else {
            setSelectedChat(null);
        }
        loadChats();
    }, [filterStatus, dateFilter]);

    // Garante que selectedChat esteja sempre sincronizado com a lista (essencial para botões de ação)
    useEffect(() => {
        if (selectedChat) {
            const updatedChat = chats.find(c => c.id === selectedChat.id);
            if (updatedChat && updatedChat.status !== selectedChat.status) {
                setSelectedChat(prev => ({ ...prev, status: updatedChat.status }));
            }
        }
    }, [chats]);

    useEffect(() => {
        if (selectedChat) {
            setMsgPage(1); // Reseta paginação ao trocar de chat
            loadMessages(selectedChat.id);
        }
    }, [selectedChat]);

    // --- SOCKET HANDLERS ---
    const handleSocketNewMessage = (data) => {
        const currentChat = selectedChatRef.current;
        if (currentChat && String(data.chatId) === String(currentChat.id)) {
            setMessages(prev => [...prev, data.message]);
            if (!data.message.fromMe) markAsRead(data.chatId);
        }
        // ✅ Atualiza o cache de mensagens do chat correspondente
        if (messagesCache.current[data.chatId]) {
            messagesCache.current[data.chatId] = [...messagesCache.current[data.chatId], data.message];
        }
        // ✅ Atualiza apenas o chat específico, sem recarregar tudo
        setChats(prev => prev.map(c =>
            String(c.id) === String(data.chatId)
                ? { ...c, lastMessage: data.message, lastMessageTime: new Date(), unreadCount: (currentChat && String(c.id) === String(currentChat.id)) ? 0 : (c.unreadCount || 0) + (!data.message.fromMe ? 1 : 0) }
                : c
        ));
    };

    const handleSocketChatUpdate = (data) => {
        // ✅ Atualiza chat na lista sem recarregar tudo
        setChats(prev => {
            const chatToUpdate = prev.find(c => String(c.id) === String(data.id));
            if (chatToUpdate && chatToUpdate.status === 'waiting' && data.status && data.status !== 'waiting') {
                setCounts(currentCounts => ({ ...currentCounts, waiting: Math.max(0, currentCounts.waiting - 1) }));
            }
            return prev.map(c => {
                if (String(c.id) !== String(data.id)) return c;
                // Preserva lastMessage/lastMessageTime se o evento não trouxer (ex: updates de status)
                const updatedLastMessage = data.lastMessage || c.lastMessage;
                const updatedLastMessageTime = data.lastMessage ? (data.lastMessage.timestamp ? new Date(data.lastMessage.timestamp * 1000) : new Date()) : c.lastMessageTime;
                return {
                    ...c,
                    ...data,
                    lastMessage: updatedLastMessage,
                    lastMessageTime: updatedLastMessageTime,
                    unreadCount: data.unreadCount !== undefined ? data.unreadCount : c.unreadCount
                };
            });
        });

        const currentChat = selectedChatRef.current;
        if (currentChat && String(data.id) === String(currentChat.id)) {
            if (data.status === 'deleted') { setSelectedChat(null); return; }
            setSelectedChat(prev => ({ ...prev, status: data.status, agentId: data.agentId, user_name: data.user_name }));

            // Se veio uma nova mensagem no evento, adiciona ao chat aberto
            if (data.lastMessage && !data.lastMessage.fromMe) {
                setMessages(prev => {
                    // Verifica se a mensagem já existe (evita duplicatas)
                    const messageExists = prev.some(m => m.id === data.lastMessage.id);
                    if (messageExists) return prev;
                    return [...prev, data.lastMessage];
                });
                markAsRead(data.id);
            }
        }
    };


    const handleSocketNewTicket = (data) => {
        const currentChat = selectedChatRef.current;
        if (currentChat && String(currentChat.id) === String(data.ticketId)) return;

        if (data.ticket) {
            // ✅ Insere o ticket diretamente na lista sem recarregar tudo
            setChats(prev => {
                const exists = prev.some(c => String(c.id) === String(data.ticket.id || data.ticket._id));
                if (exists) return prev;
                return [data.ticket, ...prev];
            });
            // ✅ Atualiza o contador de espera
            if (data.ticket.status === 'waiting') {
                setCounts(prev => ({ ...prev, waiting: prev.waiting + 1 }));
            }
        } else {
            // Fallback: recarrega caso não venham dados completos
            loadChats();
        }
    };

    // Handler para edição de mensagem
    const handleSocketMessageUpdate = (data) => {
        const currentChat = selectedChatRef.current;
        if (currentChat && String(data.chatId) === String(currentChat.id)) {
            setMessages(prev => prev.map(msg =>
                msg.id === data.message.id ? { ...msg, ...data.message } : msg
            ));
        }
        // Atualiza o cache também
        if (messagesCache.current[data.chatId]) {
            messagesCache.current[data.chatId] = messagesCache.current[data.chatId].map(msg =>
                msg.id === data.message.id ? { ...msg, ...data.message } : msg
            );
        }
        // Atualiza preview na lista lateral se for a última mensagem
        setChats(prev => prev.map(c =>
            String(c.id) === String(data.chatId) && c.lastMessage?.id === data.message.id
                ? { ...c, lastMessage: data.message }
                : c
        ));
    };

    // --- API CALLS ---
    const markAsRead = async (chatId) => {
        try {
            await api.put(`/chats/${chatId}/read`);
            // Atualiza contador local e lista de chats
            setChats(prev => {
                const target = prev.find(c => String(c.id) === String(chatId));
                if (!target || target.unreadCount === 0) return prev;

                // Atualiza o contador global subtraindo o que foi lido
                setCounts(currentCounts => ({
                    ...currentCounts,
                    unreadActive: Math.max(0, currentCounts.unreadActive - target.unreadCount)
                }));

                return prev.map(c =>
                    String(c.id) === String(chatId) ? { ...c, unreadCount: 0 } : c
                );
            });
        } catch (error) {
            console.error('[DEBUG markAsRead] ERRO:', error);
        }
    };

    const loadMessages = async (chatId) => {
        // ✅ CACHE: Exibe mensagens imediatamente do cache se disponível
        if (messagesCache.current[chatId]) {
            setMessages(messagesCache.current[chatId]);
            setLoadingMessages(false);
            // Atualiza em background silenciosamente
            api.get(`/chats/${chatId}/messages`).then(res => {
                const fresh = Array.isArray(res.data) ? res.data : [];
                messagesCache.current[chatId] = fresh;
                // Só atualiza o state se ainda estiver no mesmo chat
                if (String(selectedChatRef.current?.id) === String(chatId)) {
                    setMessages(fresh);
                }
                markAsRead(chatId);
            }).catch(() => { });
            return;
        }
        // Sem cache: mostra loading e busca normalmente
        try {
            setLoadingMessages(true);
            const res = await api.get(`/chats/${chatId}/messages`);
            const data = Array.isArray(res.data) ? res.data : [];
            messagesCache.current[chatId] = data;
            setMessages(data);
            setLoadingMessages(false); // Libera o spinner antes pois a leitura ocorre em 2º plano
            markAsRead(chatId);
        } catch (error) {
            if (error.response && error.response.status === 404) { setSelectedChat(null); loadChats(); }
            setMessages([]);
        } finally {
            setLoadingMessages(false);
        }
    };

    // Recarrega automaticamente ao mudar de aba ou as datas
    useEffect(() => {
        loadChats();
    }, [filterStatus, dateFilter.start, dateFilter.end]);

    // Lógica para aberturas forçadas por Atendimentos Preferenciais
    useEffect(() => {
        if (ticketToOpen && chats.length > 0) {
            const match = chats.find(c => c.id === ticketToOpen || c._id === ticketToOpen);
            if (filterStatus !== 'open') {
                setFilterStatus('open');
            }
            if (match) {
                // Atraso sutil para garantir a correta renderização na aba
                setTimeout(() => setSelectedChat(match), 50);
                if (typeof clearTicketToOpen === 'function') clearTicketToOpen();
            }
        }
    }, [ticketToOpen, chats, filterStatus, clearTicketToOpen]);

    const loadChats = async () => {
        try {
            const params = {};
            if (filterStatus === 'finished' || filterStatus === 'canceled') {
                params.status = filterStatus; params.startDate = dateFilter.start; params.endDate = dateFilter.end;
            }
            else if (filterStatus === 'paused') { params.status = 'paused'; }
            else { params.status = 'active'; } // 'active' retorna: waiting, open, active, paused, external
            
            // DEBUG: mostra o currentUser e o que está sendo enviado
            const myId = String(currentUser?._id || currentUser?.id || 'N/A');
            console.log('[loadChats] currentUser id:', myId, '| companyId:', String(currentUser?.companyId || 'N/A'));
            console.log('[loadChats] Buscando chats com params:', params);
            
            const response = await api.get('/chats', { params });
            const data = Array.isArray(response.data) ? response.data : [];
            
            console.log('[loadChats] Chats recebidos do backend:', data.length);
            if (data.length > 0) {
                console.log('[loadChats] Exemplo:', { id: data[0]._id || data[0].id, status: data[0].status, agentId: String(data[0].agentId) });
            }
            
            setChats(data);
            const waitingCount = data.filter(c => c.status === 'waiting').length;
            const activeUnreadCount = data.filter(c => ['open', 'active', 'paused'].includes(c.status)).reduce((acc, chat) => acc + (chat.unreadCount || 0), 0);
            const externalCount = data.filter(c => c.status === 'external').length;
            const pausedCount = data.filter(c => c.status === 'paused').length;
            setCounts({ waiting: waitingCount, unreadActive: activeUnreadCount, external: externalCount, paused: pausedCount });
        } catch (error) {
            console.error('[loadChats] ERRO ao carregar chats:', error?.response?.status, error?.response?.data || error?.message);
            setChats([]);
        }
    };

    const loadClients = async () => { try { const res = await api.get('/clients?limit=1000'); const data = res.data; setClientsList(Array.isArray(data) ? data : (data.clients || [])); } catch (error) { } };
    const loadUsers = async () => { try { const res = await api.get('/users'); setUsersList(Array.isArray(res.data) ? res.data : []); } catch (error) { } };
    const loadQuickMessages = async () => { try { const res = await api.get('/quick-messages'); setQuickMessages(Array.isArray(res.data) ? res.data : []); } catch (error) { } };
    const loadCategories = async () => { try { const res = await api.get('/categories'); setCategoriesList(Array.isArray(res.data) ? res.data : []); } catch (error) { } };


    // --- HANDLERS E AÇÕES ---
    const handleMessageStatusUpdate = (data) => { setMessages(prev => prev.map(msg => msg.id === data.messageId ? { ...msg, status: data.status } : msg)); };
    // ✅ CORRIGIDO: usa selectedChatRef para evitar stale closure
    const handleMessageReaction = (data) => {
        const currentChat = selectedChatRef.current;
        if (currentChat && data.chatId === currentChat.id) {
            setMessages(prev => prev.map(msg => msg.id === data.messageId ? { ...msg, reaction: data.reaction } : msg));
        }
    };
    // Handler para mensagem apagada (por nós via "apagar p/ todos" ou pelo cliente via WhatsApp)
    const handleMessageDeleted = (data) => {
        const currentChat = selectedChatRef.current;
        if (currentChat && String(data.chatId) === String(currentChat.id)) {
            setMessages(prev => prev.map(msg => {
                const isMatch = (data.messageId && msg.id === data.messageId) ||
                    (data.messageTimestamp && String(msg.timestamp) === String(data.messageTimestamp));
                if (!isMatch) return msg;
                // Se a msg é do cliente (não é fromMe), preserva o conteúdo original
                if (!msg.fromMe) return { ...msg, deleted: true };
                // Se é nossa msg, substitui conteúdo
                return { ...msg, deleted: true, body: 'Mensagem apagada', mediaUrl: null };
            }));
        }
        // Atualiza cache
        if (messagesCache.current[data.chatId]) {
            messagesCache.current[data.chatId] = messagesCache.current[data.chatId].map(msg => {
                const isMatch = (data.messageId && msg.id === data.messageId) ||
                    (data.messageTimestamp && String(msg.timestamp) === String(data.messageTimestamp));
                if (!isMatch) return msg;
                if (!msg.fromMe) return { ...msg, deleted: true };
                return { ...msg, deleted: true, body: 'Mensagem apagada', mediaUrl: null };
            });
        }
    };

    // ✏️ Handler de mensagem editada via socket
    const handleMessageEdited = (data) => {
        const currentChat = selectedChatRef.current;
        if (currentChat && String(data.chatId) === String(currentChat.id)) {
            setMessages(prev => prev.map(msg => {
                const isMatch = (data.messageId && msg.id === data.messageId) ||
                    (data.messageTimestamp && String(msg.timestamp) === String(data.messageTimestamp));
                if (!isMatch) return msg;
                return { ...msg, body: data.newBody, isEdited: true };
            }));
        }
        if (messagesCache.current[data.chatId]) {
            messagesCache.current[data.chatId] = messagesCache.current[data.chatId].map(msg => {
                const isMatch = (data.messageId && msg.id === data.messageId) ||
                    (data.messageTimestamp && String(msg.timestamp) === String(data.messageTimestamp));
                if (!isMatch) return msg;
                return { ...msg, body: data.newBody, isEdited: true };
            });
        }
    };
    const handleSendReaction = async (message, emoji) => {
        if (!selectedChat) return;
        // Função auxiliar para identificar a mensagem exata (msg.id pode ser undefined)
        const isSameMsg = (m) => {
            if (message.id && m.id) return m.id === message.id;
            return String(m.timestamp) === String(message.timestamp) && m.fromMe === message.fromMe;
        };
        try {
            setMessages(prev => prev.map(m => isSameMsg(m) ? { ...m, reaction: emoji ? { emoji, fromMe: true, timestamp: new Date() } : null } : m));
            await api.post(`/chats/${selectedChat.id}/messages/reaction`, { messageId: message.id, messageTimestamp: message.timestamp, emoji });
        } catch (error) { console.error('[REACTION ERROR]', error); }
    };

    const handleSendMessage = async (e) => {
        e?.preventDefault(); if (!selectedChat || isSending) return;
        const body = inputText.trim(); if (!body && !recordedAudio) return;

        // ✏️ Se estiver em modo de edição, chama handleEditMessage
        if (editingMessage) {
            await handleEditMessage(body);
            return;
        }

        try {
            setIsSending(true);
            const res = await api.post(`/chats/${selectedChat.id}/messages`, { body: body, replyTo: replyingTo ? replyingTo : null });
            setInputText(''); setReplyingTo(null); setRecordedAudio(null);
            const newMsg = { ...res.data, status: 'sent', fromMe: true, senderName: currentUser.name };
            setMessages(prev => [...prev, newMsg]);
            // ✅ Atualiza o cache também ao enviar
            if (messagesCache.current[selectedChat.id]) {
                messagesCache.current[selectedChat.id] = [...messagesCache.current[selectedChat.id], newMsg];
            }
            if (selectedChat.status === 'waiting') await handleStatusChange('accept');
        } catch (error) { alert('Erro ao enviar'); } finally { setIsSending(false); setTimeout(() => { document.querySelector('input[placeholder="Digite..."]')?.focus(); }, 100); }
    };

    // ✏️ Função de editar mensagem
    const handleEditMessage = async (newBody) => {
        if (!selectedChat || !editingMessage || !newBody) return;
        try {
            setIsSending(true);
            const isSameMsg = (m) => {
                if (editingMessage.id && m.id) return m.id === editingMessage.id;
                return String(m.timestamp) === String(editingMessage.timestamp) && m.fromMe === editingMessage.fromMe;
            };
            await api.put(`/chats/${selectedChat.id}/messages/edit`, {
                messageId: editingMessage.id,
                messageTimestamp: editingMessage.timestamp,
                newBody: newBody
            });
            // Atualiza localmente
            setMessages(prev => prev.map(m => isSameMsg(m) ? { ...m, body: newBody, isEdited: true } : m));
            if (messagesCache.current[selectedChat.id]) {
                messagesCache.current[selectedChat.id] = messagesCache.current[selectedChat.id].map(m => isSameMsg(m) ? { ...m, body: newBody, isEdited: true } : m);
            }
            setEditingMessage(null);
            setInputText('');
        } catch (error) {
            alert('Erro ao editar mensagem');
        } finally {
            setIsSending(false);
        }
    };

    const handleDeleteMessage = async (message, type) => {
        if (!selectedChat) return;
        // Função auxiliar para identificar a mensagem exata (msg.id pode ser undefined)
        const isSameMsg = (m) => {
            if (message.id && m.id) return m.id === message.id;
            return String(m.timestamp) === String(message.timestamp) && m.fromMe === message.fromMe;
        };
        try {
            setMessages(prev => prev.map(m => {
                if (!isSameMsg(m)) return m;
                if (type === 'everyone') return { ...m, deleted: true, body: 'Mensagem apagada', mediaUrl: null };
                return null; // apagar p/ mim: remove do array
            }).filter(Boolean));
            await api.delete(`/chats/${selectedChat.id}/messages`, { data: { messageId: message.id, messageTimestamp: message.timestamp, deleteFor: type } });
        } catch (error) { console.error('[DELETE ERROR]', error); loadMessages(selectedChat.id); }
    };
    const handleForwardMessage = async (targetChatId) => { try { const msg = forwardModal.message; await api.post(`/chats/${targetChatId}/messages/forward`, { originalMessage: { type: msg.type || 'text', body: msg.body, mediaUrl: msg.mediaUrl, fileName: msg.fileName } }); setForwardModal({ isOpen: false, message: null }); } catch (error) { console.error('[FORWARD ERROR]', error); } };

    const handlePaste = (e) => { if (!selectedChat) return; const items = e.clipboardData?.items; if (!items) return; for (let i = 0; i < items.length; i++) { if (items[i].type.indexOf('image') !== -1) { e.preventDefault(); const blob = items[i].getAsFile(); if (blob) { setImageToEdit(blob); setShowImageEditor(true); } return; } } };

    // =================================================================================
    // 1. UPLOAD DE ARQUIVOS (VÍDEOS, ÁUDIOS, DOCS) - CORRIGIDO
    // =================================================================================
    // Cole dentro do ChatScreen.js

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !selectedChat) return;

        if (file.type.startsWith('image/')) {
            setImageToEdit(file); setShowImageEditor(true); e.target.value = ''; return;
        }

        const formData = new FormData();
        formData.append('type', file.type.startsWith('video/') ? 'video' : file.type.startsWith('audio/') ? 'audio' : 'document');
        formData.append('chatId', selectedChat.id);
        formData.append('file', file);

        try {
            // SEM HEADERS!
            const res = await api.post(`/chats/${selectedChat.id}/messages/media`, formData, {
                onUploadProgress: (ev) => setUploadProgress(Math.round((ev.loaded * 100) / ev.total))
            });
            setMessages(prev => [...prev, res.data]); setUploadProgress(0);
        } catch (error) { setUploadProgress(0); alert('Erro no envio.'); }
        e.target.value = '';
    };

    const handleSaveEditedImage = async (editedImageFile, caption = '') => {
        if (!selectedChat) return;
        const formData = new FormData();
        formData.append('type', 'image');
        formData.append('chatId', selectedChat.id);
        formData.append('file', editedImageFile);
        if (caption) {
            formData.append('caption', caption);
        }

        try {
            // SEM HEADERS!
            const res = await api.post(`/chats/${selectedChat.id}/messages/media`, formData, {
                onUploadProgress: (ev) => setUploadProgress(Math.round((ev.loaded * 100) / ev.total))
            });
            setMessages(prev => [...prev, res.data]); setUploadProgress(0); setShowImageEditor(false); setImageToEdit(null);
        } catch (error) { setUploadProgress(0); alert('Erro ao enviar imagem.'); }
    };

    const startRecording = async () => { try { if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) { alert('Erro: O navegador bloqueou o acesso ao microfone porque o sistema não está rodando em um ambiente seguro (HTTPS/localhost).'); return; } const stream = await navigator.mediaDevices.getUserMedia({ audio: true }); const mimeType = MediaRecorder.isTypeSupported('audio/ogg;codecs=opus') ? 'audio/ogg;codecs=opus' : MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm'; mediaRecorderRef.current = new MediaRecorder(stream, { mimeType }); audioChunksRef.current = []; mediaRecorderRef.current.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); }; mediaRecorderRef.current.onstop = () => { const audioBlob = new Blob(audioChunksRef.current, { type: mimeType }); setRecordedAudio({ blob: audioBlob, url: URL.createObjectURL(audioBlob) }); stream.getTracks().forEach(track => track.stop()); }; mediaRecorderRef.current.start(250); setIsRecording(true); recordingIntervalRef.current = setInterval(() => setRecordingTime(prev => prev + 1), 1000); } catch (error) { console.error('[startRecording] Erro:', error); if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') { alert('Permissão de microfone negada. Por favor, permita o acesso ao microfone nas configurações do navegador.'); } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') { alert('Nenhum microfone encontrado. Verifique se o dispositivo está conectado.'); } else { alert('Não foi possível iniciar a gravação: ' + (error.message || error.name)); } } };
    const stopRecording = () => { if (mediaRecorderRef.current && isRecording) { mediaRecorderRef.current.stop(); setIsRecording(false); clearInterval(recordingIntervalRef.current); setRecordingTime(0); } };

    // 3. ENVIO DE ÁUDIO CORRIGIDO
    const handleSendAudio = async () => {
        if (!recordedAudio || !selectedChat) return;
        const formData = new FormData();
        formData.append('type', 'audio');
        formData.append('chatId', selectedChat.id);
        // Envia como .ogg para garantir compatibilidade com WhatsApp PTT
        formData.append('file', recordedAudio.blob, 'audio.ogg');

        try {
            // SEM HEADERS MANUAIS
            const res = await api.post(`/chats/${selectedChat.id}/messages/media`, formData);
            setMessages(prev => [...prev, res.data]);
            setRecordedAudio(null);
        } catch (error) {
            console.error("Erro audio:", error);
            alert("Erro ao enviar áudio");
        }
    };

    // --- GESTÃO DE CLIENTE ---
    // Função handleOpenEditModal antiga removida em favor da nova implementação


    const checkClientDataAndExecute = (actionType, actionPayload = null) => {
        if (!selectedChat) return;
        if (actionType === 'edit_only') { handleOpenEditModal(false); return; }

        const isRequired = companyData?.settings?.clientRegisterRequired;
        const currentPhone = selectedChat.contact_id || '';
        const currentName = selectedChat.name || '';
        const isLid = currentPhone.includes('@lid');
        const isMissingName = !currentName || currentName === currentPhone || currentName === 'Contato WhatsApp';

        // 🆕 Verifica empresa: busca na lista de clientes
        const client = clientsList.find(c => cleanPhone(c.phone) === cleanPhone(currentPhone));
        const isMissingCompany = !client?.company || client.company.trim() === '';

        if (isRequired && (isMissingName || isLid || isMissingCompany)) {
            setPendingAction({ type: actionType, payload: actionPayload });
            handleOpenEditModal(true);
        } else {
            executeAction(actionType, actionPayload);
        }
    };

    const handleSaveClientData = async (e) => {
        e.preventDefault();
        const clientToUpdate = clientFormData._id || clientsList.find(c => cleanPhone(c.phone) === cleanPhone(selectedChat.contact_id))?._id;

        if (!clientToUpdate) { alert("Erro: Cliente não encontrado na base de dados. Recarregue a página."); return; }

        try {
            await api.put(`/clients/${clientToUpdate}`, {
                name: clientFormData.name,
                phone: clientFormData.phone,
                company: clientFormData.company
            });
            setSelectedChat(prev => ({ ...prev, name: clientFormData.name }));
            loadChats(); loadClients();
            setIsClientModalOpen(false);
            if (pendingAction) { executeAction(pendingAction.type, pendingAction.payload); setPendingAction(null); }
        } catch (error) { console.error(error); alert("Erro ao salvar dados do cliente."); }
    };

    const executeAction = (actionType, payload) => {
        // 🆕 Verificar obrigatoriedade de categorização ao finalizar, cancelar, pausar ou transferir para externo
        const categorizationRequired = companyData?.settings?.categorizationRequired;
        if (categorizationRequired && (actionType === 'finish' || actionType === 'cancel' || actionType === 'pause' || actionType === 'external')) {
            setSelectedCategoryId('');
            setSelectedSubcategoryId('');
            setCategoryModal({ isOpen: true, actionType });
            return;
        }
        switch (actionType) {
            case 'finish': setIsFinishModalOpen(true); break;
            case 'pause': 
                setPauseReason('');
                setActionModal({ isOpen: true, type: 'pause' }); 
                break;
            case 'transfer': setIsTransferModalOpen(true); break;
            case 'external': setActionModal({ isOpen: true, type: 'external' }); break;
            case 'cancel': setActionModal({ isOpen: true, type: 'cancel' }); break;
            case 'reopen': handleStatusChange('reopen'); break;
            case 'resume': handleStatusChange('resume'); break;
            default: break;
        }
    };

    // --- FUNÇÃO DE HISTÓRICO ---
    const handleLoadHistory = async () => {
        if (!selectedChat || !selectedChat.clientId) return;

        setLoadingHistory(true);
        try {
            const res = await api.get(`/chats/client/${selectedChat.clientId}/history`);
            setHistoryModal({ isOpen: true, data: res.data || [] });
        } catch (error) {
            console.error('[HISTORY ERROR]', error);
            alert('Erro ao carregar histórico');
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleOpenHistoryTicket = async (ticketId) => {
        try {
            const res = await api.get(`/chats/${ticketId}/messages`);
            setMessages(res.data || []);
            setHistoryModal({ isOpen: false, data: [] });
            // Busca dados do ticket para exibir
            const ticketRes = await api.get(`/chats`);
            const ticket = ticketRes.data.find(t => t.id === ticketId);
            if (ticket) setSelectedChat(ticket);
        } catch (error) {
            console.error('[OPEN HISTORY TICKET ERROR]', error);
            alert('Erro ao abrir atendimento');
        }
    };

    // --- ABRIR MODAL DE CLIENTE ---
    const handleOpenClientForm = () => {
        if (!selectedChat) return;

        const phone = selectedChat.contact_id || selectedChat.phone;
        const name = selectedChat.name || '';

        // Tenta encontrar cliente na lista se não tiver ID direto
        const client = clientsList.find(c => cleanPhone(c.phone) === cleanPhone(phone)) || {};
        const finalClientId = selectedChat.clientId || client._id || client.id;

        setClientFormModal({
            isOpen: true,
            clientId: finalClientId || null,
            phone: client.phone || phone,
            name: client.name || name,
            company: client.company || '',
            email: client.email || '',
            notes: client.notes || '',
            ignoreMetrics: client.ignoreMetrics || false,
            usePreferredAgent: client.usePreferredAgent || false, // 🆕
            preferredAgentId: client.preferredAgentId || '', // 🆕
            lid: client.lid || ''
        });
    };

    const handleOpenEditModal = (fromButton = true) => {
        // Mesma função
        handleOpenClientForm();
    };

    const handleClientFormSuccess = async () => {
        // Recarrega dados após salvar cliente
        await loadChats();
        await loadClients(); // 🆕 Atualiza lista de clientes para pegar empresa recém salva
        if (selectedChat) {
            const updated = chats.find(c => c.id === selectedChat.id);
            if (updated) setSelectedChat(updated);
        }
        // 🆕 Executa a ação pendente (finalizar, cancelar, etc.) após o save
        if (pendingAction) {
            const action = pendingAction;
            setPendingAction(null);
            setTimeout(() => executeAction(action.type, action.payload), 300);
        }
    };

    // --- FUNÇÕES DE STATUS E OPERAÇÕES ---
    const handleStatusChange = async (action, extraData = {}) => {
        if (!selectedChat) return;
        setIsGlobalLoading(true);
        try {
            let newStatus = selectedChat.status;
            switch (action) {
                case 'accept':
                    newStatus = 'open';
                    break;
                case 'finish': newStatus = 'ended'; break;
                case 'pause': newStatus = 'paused'; break;
                case 'resume':
                    newStatus = 'open';
                    break;
                case 'external': newStatus = 'external'; break;
                case 'reopen':
                    newStatus = 'open';
                    break;
                case 'cancel': newStatus = 'canceled'; break;
                default: return;
            }
            if (action === 'pause' && !extraData.pauseReason) {
                // Se não vier no extraData, usa o state
                extraData.pauseReason = pauseReason;
            }
            const response = await api.put(`/chats/${selectedChat.id}`, { status: newStatus, ...extraData });
            if (['ended', 'canceled', 'finished'].includes(newStatus)) { 
                if (selectedChat.status === 'waiting') setCounts(prev => ({ ...prev, waiting: Math.max(0, prev.waiting - 1) }));
                setSelectedChat(null); 
                setChats(prev => prev.filter(c => c.id !== selectedChat.id)); 
            }
            else {
                if (selectedChat.status === 'waiting' && newStatus !== 'waiting') setCounts(prev => ({ ...prev, waiting: Math.max(0, prev.waiting - 1) }));
                const updatedChat = response.data;
                setSelectedChat(prev => ({ ...prev, status: newStatus, agentId: updatedChat.agentId, user_name: updatedChat.user_name }));
                setChats(prev => prev.map(c => c.id === selectedChat.id ? { ...c, status: newStatus } : c));

                // MUDANÇA DE ABA AUTOMÁTICA (Só depois de atualizar o estado do chat)
                if (['accept', 'resume', 'reopen'].includes(action)) {
                    setFilterStatus('open');
                }
            }
            // ✅ OTIMIZADO: loadChats() removido aqui pois o estado já foi atualizado localmente acima
            setIsFinishModalOpen(false); setActionModal({ isOpen: false, type: null });
        } catch (error) {
            if (error.response && error.response.status === 404) { alert("Este atendimento não existe mais."); setSelectedChat(null); loadChats(); } else { alert("Erro ao atualizar o chamado."); }
        } finally { setIsGlobalLoading(false); }
    };

    const handleTransfer = async () => { if (!transferTarget || !selectedChat) return; setIsGlobalLoading(true); try { await api.put(`/chats/${selectedChat.id}/transfer`, { agentId: transferTarget }); setIsTransferModalOpen(false); setTransferTarget(''); setSelectedChat(null); loadChats(); } catch (error) { } finally { setIsGlobalLoading(false); } };
    const handleStartConversation = async () => { if (!newChatClient) return; try { const res = await api.post('/chats/start', { clientId: newChatClient, initialMessage: newChatMessage, initiatedBy: currentUser._id }); setIsStartChatModalOpen(false); setNewChatClient(''); setNewChatMessage(''); setFilterStatus('open'); await loadChats(); setSelectedChat(res.data); } catch (error) { } };
    const handleAddAttendant = async () => { if (!selectedChat || selectedAttendants.length === 0) return; try { await api.put(`/chats/${selectedChat.id}/add-attendants`, { attendants: selectedAttendants }); setAddAttendantModal({ isOpen: false }); setSelectedAttendants([]); loadChats(); } catch (error) { } };
    const handleOpenOsModal = () => { 
        if (!selectedChat) return; 
        const client = clientsList.find(c => cleanPhone(c.phone) === cleanPhone(selectedChat.contact_id)); 
        const lastClientMessage = messages.filter(m => !m.fromMe && m.body && !m.body.includes('📞')).pop(); 
        const getLocalYYYYMMDD = (d) => { if(!d)return''; const year=d.getFullYear(); const month=String(d.getMonth()+1).padStart(2,'0'); const day=String(d.getDate()).padStart(2,'0'); return `${year}-${month}-${day}`; };
        const now = new Date();
        setOsModal({ isOpen: true, data: { clientName: selectedChat.name || client?.name || '', companyName: client?.company || '', requestDescription: lastClientMessage?.body || '', openingDate: getLocalYYYYMMDD(now), openingTime: now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) } }); 
    };
    const handleSaveOs = async () => { try { const osData = { ...osModal.data, clientPhone: selectedChat.contact_id, status: 'open', createdBy: currentUser._id }; await api.post('/work-orders', osData); setOsModal({ isOpen: false, data: null }); alert('Ordem de Serviço criada com sucesso!'); } catch (error) { } };

    // Função handleLoadHistory duplicada removida


    const findClientByPhone = async () => {
        try {
            const phone = selectedChat.contact_id || selectedChat.phone;
            const client = clientsList.find(c => cleanPhone(c.phone) === cleanPhone(phone));
            return client?._id || client?.id;
        } catch (error) { return null; }
    };

    // Funções duplicadas removidas


    // Função auxiliar para renderizar mensagens no histórico (simplificada)
    const renderMessageBubble = (msg, idx) => {
        return (
            <div key={msg.id || idx} className={`flex ${msg.fromMe ? 'justify-end' : 'justify-start'} mb-2`}>
                <div className={`rounded-lg p-2 shadow-sm relative break-words whitespace-pre-wrap overflow-hidden max-w-[85%] ${msg.fromMe ? 'bg-[#d9fdd3] rounded-tr-none' : 'bg-white rounded-tl-none'}`}>
                    {msg.fromMe && msg.senderName && <p className="text-[10px] font-bold text-blue-800 mb-1 opacity-80 uppercase">{msg.senderName}</p>}

                    {msg.type === 'call' ? <div className="p-2 italic text-gray-500">📞 Chamada de voz/vídeo</div> : (
                        <>
                            {msg.type === 'image' && msg.mediaUrl && <img src={`${BASE_MEDIA_URL}${msg.mediaUrl}`} alt="Imagem" className="rounded-lg mb-2 cursor-pointer max-w-full" onClick={() => window.open(`${BASE_MEDIA_URL}${msg.mediaUrl}`, '_blank')} />}
                            {msg.type === 'video' && msg.mediaUrl && <div className="rounded-lg overflow-hidden mb-2 max-w-full bg-black"><video controls src={`${BASE_MEDIA_URL}${msg.mediaUrl}`} className="w-full h-auto max-h-[400px] min-w-[250px]" /></div>}
                            {msg.type === 'audio' && msg.mediaUrl && <audio controls src={`${BASE_MEDIA_URL}${msg.mediaUrl}`} className="h-10 min-w-[260px] mt-1 accent-blue-600 cursor-pointer z-20 relative" />}
                            {msg.type === 'document' && msg.mediaUrl && <a href={`${BASE_MEDIA_URL}${msg.mediaUrl}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 bg-black/5 rounded hover:bg-black/10"><Paperclip className="w-4 h-4" /> <span className="text-sm">{msg.fileName || 'Documento'}</span></a>}
                            {msg.type === 'contact' && <ContactMessage message={msg} />}
                            {msg.type === 'contact_array' && msg.body.split('\n---\n').map((cBody, i) => (
                                <ContactMessage key={i} message={{ ...msg, body: cBody }} />
                            ))}

                            {!['call', 'audio', 'image', 'video', 'document', 'contact', 'contact_array'].includes(msg.type) && msg.body && <LinkifiedText text={msg.body} />}
                        </>
                    )}

                    <div className="flex items-center justify-end gap-1 mt-1 text-[10px] text-gray-500">
                        {msg.isEdited && <span className="italic mr-1">Editada</span>}
                        <span>{new Date(msg.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
                        {msg.fromMe && <CheckCheck className={`w-3 h-3 ${msg.read ? 'text-blue-500' : 'text-gray-400'}`} />}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="flex h-full bg-gray-50 relative overflow-hidden">
            {isGlobalLoading && (<div className="absolute inset-0 z-[10000] bg-black/40 flex items-center justify-center backdrop-blur-sm"><div className="bg-white p-6 rounded-2xl shadow-2xl flex flex-col items-center gap-4 animate-in zoom-in-95"><Loader2 className="w-10 h-10 text-blue-600 animate-spin" /><span className="font-bold text-gray-700 text-lg">Processando...</span></div></div>)}

            {/* SIDEBAR */}
            <div className={`${selectedChat ? 'hidden md:flex' : 'flex'} w-full md:w-96 bg-white border-r flex-col h-full z-10`}>
                <div className="p-4 border-b bg-gradient-to-r from-blue-600 to-blue-700 text-white shrink-0">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-lg font-bold flex items-center gap-2">
                            <MessageSquare className="w-5 h-5" /> Conversas
                        </h2>
                        <div className="flex items-center gap-2">
                            {/* BOTÃO DE MUTE/UNMUTE */}
                            {setIsMuted && (
                                <button
                                    onClick={() => setIsMuted(!isMuted)}
                                    className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-colors"
                                    title={isMuted ? "Ativar Sons" : "Silenciar"}
                                >
                                    {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                                </button>
                            )}
                            <button onClick={() => setIsStartChatModalOpen(true)} className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-colors">
                                <PlusCircle className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                    <div className="relative"><input type="text" placeholder="Buscar..." value={searchTerm} onChange={(e) => handleSearchChange(e.target.value)} className="w-full pl-10 pr-4 py-2 rounded-lg bg-white/20 text-white placeholder-white/70 border border-white/30 focus:bg-white/30 focus:outline-none" /><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/70" /></div>
                </div>

                <div className="p-3 border-b bg-gray-50 flex gap-2 overflow-x-auto shrink-0">
                    {[{ id: 'waiting', icon: Clock, title: 'Pendentes', count: counts.waiting }, { id: 'open', icon: MessageSquare, title: 'Meus', count: counts.unreadActive, isMsgCount: true }, { id: 'paused', icon: Pause, title: 'Pausados', count: counts.paused }, { id: 'external', icon: ExternalLink, title: 'Externos', count: counts.external }, { id: 'canceled', icon: XCircle, title: 'Cancelados' }, { id: 'finished', icon: CheckCircle, title: 'Finalizados' }].map(filter => { const Icon = filter.icon; return (<button key={filter.id} onClick={() => setFilterStatus(filter.id)} title={filter.title} className={`p-2.5 rounded-lg transition-all relative shrink-0 ${filterStatus === filter.id ? 'bg-blue-600 text-white shadow-md' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'}`}><Icon className="w-5 h-5" />{filter.count > 0 && <span className={`absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full border border-white ${filter.isMsgCount ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>{filter.count}</span>}</button>); })}
                </div>

                {(filterStatus === 'finished' || filterStatus === 'canceled') && (<div className="p-2 border-b bg-blue-50 flex items-center gap-2 animate-in slide-in-from-top-2 shrink-0"><div className="flex-1"><p className="text-[10px] text-gray-500 font-bold uppercase ml-1">De:</p><input type="date" value={dateFilter.start} onChange={(e) => setDateFilter({ ...dateFilter, start: e.target.value })} className="w-full p-1 text-xs border rounded outline-none" /></div><div className="flex-1"><p className="text-[10px] text-gray-500 font-bold uppercase ml-1">Até:</p><input type="date" value={dateFilter.end} onChange={(e) => setDateFilter({ ...dateFilter, end: e.target.value })} className="w-full p-1 text-xs border rounded outline-none" /></div><button onClick={loadChats} className="self-end p-1.5 mb-0.5 bg-blue-600 text-white rounded hover:bg-blue-700 shadow-sm"><Search className="w-4 h-4" /></button></div>)}

                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    {filteredChats.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-40 text-gray-400"><Inbox className="w-10 h-10 mb-2 opacity-30" /><p className="text-sm">Nenhuma conversa aqui.</p></div>
                    ) : (
                        filteredChats.map(chat => (
                            <div key={chat.id} onClick={() => setSelectedChat(chat)} className={`px-2 py-1.5 border-b cursor-pointer transition-all hover:bg-blue-50 flex gap-2 items-center ${selectedChat?.id === chat.id ? 'bg-blue-50 border-l-4 border-l-blue-600' : 'border-l-4 border-transparent'}`}>
                                <div className="relative shrink-0">{chat.profilePicUrl ? <img src={chat.profilePicUrl} className="w-9 h-9 rounded-full object-cover border border-gray-200" alt="" /> : <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center font-bold text-gray-500 text-sm border border-gray-300">{chat.name?.charAt(0).toUpperCase()}</div>}{chat.unreadCount > 0 && <span className="absolute top-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-white rounded-full"></span>}</div>
                                <div className="flex-1 min-w-0 flex flex-col justify-center">
                                    <div className="flex justify-between items-baseline">
                                        <h3 className={`truncate text-[13px] leading-none ${chat.unreadCount > 0 ? 'font-extrabold text-black' : 'font-bold text-gray-800'}`}>
                                            {chat.name}
                                        </h3>
                                        <div className="flex items-center gap-1 shrink-0 ml-1">
                                            {getStatusBadge(chat.status)}
                                            <span className={`text-[10px] ${chat.unreadCount > 0 ? 'text-green-600 font-bold' : 'text-gray-400'}`}>
                                                {chat.lastMessageTime ? new Date(chat.lastMessageTime).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : ''}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Empresa & Telefone em linhas separadas */}
                                    {(() => {
                                        const chatClient = clientsList.find(c => cleanPhone(c.phone) === cleanPhone(chat.contact_id));
                                        return (
                                            <div className="flex flex-col leading-tight mt-0.5">
                                                {chatClient?.company && (
                                                    <span className="text-[10px] text-blue-700 font-extrabold truncate w-full">
                                                        🏢 {formatCompanyName(chatClient.company)}
                                                    </span>
                                                )}
                                                <span className="text-[10px] text-blue-500 font-medium truncate w-full">
                                                    {cleanPhoneDisplay(chat.contact_id)}
                                                </span>
                                            </div>
                                        );
                                    })()}

                                    <div className="flex justify-between items-center mt-0.5">
                                        <p className={`text-[10px] leading-tight truncate w-[85%] ${chat.unreadCount > 0 ? 'font-bold text-gray-900' : 'text-gray-500'}`}>
                                            {typeof chat.lastMessage === 'string' ? chat.lastMessage : (chat.lastMessage?.body || chat.lastMessage?.caption || 'Mídia/Áudio')}
                                        </p>
                                        {chat.unreadCount > 0 && (<span className="bg-green-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center shadow-sm">{chat.unreadCount}</span>)}
                                    </div>

                                    {/* Preferencial logo abaixo em versão inline muito compacta */}
                                    {(() => {
                                        const chatClient = clientsList.find(c => cleanPhone(c.phone) === cleanPhone(chat.contact_id));
                                        if (chat.status === 'waiting' && chatClient?.usePreferredAgent) {
                                            const preffId = chat.preferredAgentId || chatClient.preferredAgentId;
                                            const preffUserObj = usersList.find(u => u._id === preffId);
                                            const name = preffUserObj ? preffUserObj.name : 'Desconhecido';
                                            return (
                                                <p className="text-[9px] text-orange-600 font-bold mt-0.5 truncate bg-orange-50 px-1 py-px rounded inline-block self-start border border-orange-100 uppercase tracking-tighter">
                                                    ⭐ PREF: {name}
                                                </p>
                                            );
                                        }
                                        return null;
                                    })()}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* ÁREA DE CHAT */}
            <div className={`${!selectedChat ? 'hidden md:flex' : 'flex'} flex-1 flex-col h-full relative bg-[#e5ddd5]`}>
                {!selectedChat ? (
                    <div className="flex-1 flex items-center justify-center bg-gray-100"><div className="text-center text-gray-400"><MessageSquare className="w-24 h-24 mx-auto mb-4 opacity-30" /><p className="text-lg">Selecione uma conversa</p></div></div>
                ) : (
                    <>
                        <div className="p-3 bg-white border-b flex flex-col md:flex-row md:items-center justify-between shadow-sm z-20 relative shrink-0 gap-3">
                            <div className="flex items-center gap-3 overflow-hidden group cursor-pointer w-full md:w-auto" onClick={() => handleOpenEditModal(false)}>
                                <button onClick={(e) => { e.stopPropagation(); setSelectedChat(null); }} className="md:hidden p-2 hover:bg-gray-100 rounded-full shrink-0"><ArrowLeft className="w-5 h-5" /></button>
                                {selectedChat.profilePicUrl ? <img src={selectedChat.profilePicUrl} className="w-10 h-10 rounded-full object-cover hover:opacity-80 shrink-0" onClick={(e) => { e.stopPropagation(); setViewImage(selectedChat.profilePicUrl); }} alt="" /> : <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center font-bold text-blue-600 shrink-0 border border-blue-200">{selectedChat.name?.charAt(0).toUpperCase()}</div>}
                                <div className="min-w-0">
                                    <h2 className="font-bold text-gray-800 text-lg leading-tight truncate flex items-center">
                                        {selectedChat.name}
                                        <Edit3 className="w-3 h-3 ml-2 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </h2>
                                    <p className="text-xs text-blue-600 font-semibold truncate">
                                        {(() => {
                                            const currentClient = clientsList.find(c => cleanPhone(c.phone) === cleanPhone(selectedChat.contact_id));
                                            return currentClient?.company ? formatCompanyName(currentClient.company) : cleanPhone(selectedChat.contact_id);
                                        })()}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-2 flex-wrap justify-start md:justify-end w-full md:w-auto">
                                {(selectedChat.agentId === currentUser._id || selectedChat.initiatedBy === currentUser._id || currentUser.role === 'admin') && !currentUser?.permissions?.block_chat_add_attendant && (
                                    <button onClick={() => setAddAttendantModal({ isOpen: true })} className="p-2 bg-blue-50 text-blue-700 rounded-lg"><UserPlus className="w-4 h-4" /></button>
                                )}
                                {!currentUser?.permissions?.block_chat_history && (
                                    <button onClick={handleLoadHistory} disabled={loadingHistory} className="p-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100" title="Histórico de Atendimentos">
                                        {loadingHistory ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                                    </button>
                                )}
                                {!currentUser?.permissions?.block_chat_client_edit && (
                                    <button onClick={handleOpenClientForm} className="p-2 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100" title="Cadastrar/Atualizar Cliente">
                                        <UserPlus className="w-4 h-4" />
                                    </button>
                                )}

                                {selectedChat.status === 'waiting' && <button onClick={() => handleStatusChange('accept')} className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-bold shadow-sm">Aceitar</button>}

                                {(selectedChat.status === 'open' || selectedChat.status === 'active') && <>
                                    {!currentUser?.permissions?.block_chat_pause && <button onClick={() => checkClientDataAndExecute('pause')} className="p-2 text-orange-600 hover:bg-orange-50 rounded-full bg-orange-50/50 md:bg-transparent" title="Pausar"><Pause className="w-5 h-5" /></button>}
                                    {!currentUser?.permissions?.block_chat_transfer && <button onClick={() => checkClientDataAndExecute('transfer')} className="p-2 text-blue-600 hover:bg-blue-50 rounded-full bg-blue-50/50 md:bg-transparent" title="Transferir"><ArrowLeftRight className="w-5 h-5" /></button>}
                                    {!currentUser?.permissions?.block_chat_transfer_external && <button onClick={() => checkClientDataAndExecute('external')} className="p-2 text-purple-600 hover:bg-purple-50 rounded-full bg-purple-50/50 md:bg-transparent" title="Externo"><ExternalLink className="w-5 h-5" /></button>}
                                    {!currentUser?.permissions?.block_chat_cancel && <button onClick={() => checkClientDataAndExecute('cancel')} className="p-2 text-red-600 hover:bg-red-50 rounded-full bg-red-50/50 md:bg-transparent" title="Cancelar"><XCircle className="w-5 h-5" /></button>}
                                    {!currentUser?.permissions?.block_chat_finish && <button onClick={() => checkClientDataAndExecute('finish')} className="p-2 text-green-600 hover:bg-green-50 rounded-full bg-green-50/50 md:bg-transparent" title="Finalizar"><CheckCircle className="w-5 h-5" /></button>}
                                </>}

                                {selectedChat.status === 'paused' && <button onClick={() => checkClientDataAndExecute('resume')} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-bold shadow-sm">Retomar</button>}
                                {selectedChat.status === 'external' && (
                                    <>
                                        {!currentUser?.permissions?.block_menu_work_orders && <button onClick={handleOpenOsModal} className="p-2 bg-green-50 text-green-700 rounded-lg mr-2" title="Gerar O.S."><FileText className="w-4 h-4" /></button>}
                                        <button onClick={() => checkClientDataAndExecute('reopen')} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-bold shadow-sm">Reabrir</button>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* DATA E HORA DE ABERTURA */}
                        {selectedChat.createdAt && (
                            <div className="px-4 py-2 bg-blue-50 border-b border-blue-100">
                                <p className="text-xs text-blue-700 text-center font-medium">
                                    <Clock className="w-3 h-3 inline mr-1" />
                                    Iniciado em {new Date(selectedChat.createdAt).toLocaleDateString('pt-BR')} às {new Date(selectedChat.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                </p>
                            </div>
                        )}

                        {/* LISTA DE MENSAGENS */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar bg-[url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')] bg-fixed md:bg-fixed bg-repeat">
                            {/* Loading de mensagens */}
                            {loadingMessages && (
                                <div className="flex justify-center items-center py-8">
                                    <div className="flex flex-col items-center gap-2">
                                        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                        <span className="text-xs text-gray-500">Carregando mensagens...</span>
                                    </div>
                                </div>
                            )}
                            {/* Botão de carregar mensagens mais antigas */}
                            {!loadingMessages && messages.length > MSG_PAGE_SIZE * msgPage && (
                                <div className="flex justify-center py-2">
                                    <button
                                        onClick={() => setMsgPage(p => p + 1)}
                                        className="text-xs text-blue-600 bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-full font-semibold border border-blue-200 transition-colors"
                                    >
                                        Carregar mensagens mais antigas
                                    </button>
                                </div>
                            )}
                            {(Array.isArray(messages) ? messages : [])
                                .filter(msg => msg.body || msg.mediaUrl || msg.type === 'call' || msg.deleted)
                                .slice(-(MSG_PAGE_SIZE * msgPage))
                                .map((msg, idx) => {
                                    return (
                                        <div key={msg.id || `msg-${idx}`} data-msg-id={msg.id || ''} data-msg-ts={msg.timestamp || ''} className={`flex ${msg.fromMe ? 'justify-end' : 'justify-start'} group relative mb-1`} onMouseEnter={() => setHoveredMessage(idx)} onMouseLeave={() => setHoveredMessage(null)}>
                                            <div className="relative max-w-[85%] lg:max-w-[70%]">
                                                {/* Menu de hover - não aparece para ligações ou mensagens apagadas */}
                                                {(hoveredMessage === idx || activeMenuMessageId === idx) && msg.type !== 'call' && !msg.deleted && (
                                                    <div className="absolute top-0 right-0 p-1 cursor-pointer z-20" onClick={(e) => { e.stopPropagation(); setActiveMenuMessageId(activeMenuMessageId === idx ? null : idx); }}>
                                                        <div className="bg-gradient-to-l from-black/20 to-transparent rounded-full p-1">
                                                            <ChevronDown className="w-4 h-4 text-gray-600 bg-white rounded-full shadow-sm hover:bg-gray-100" />
                                                        </div>
                                                    </div>
                                                )}
                                                {activeMenuMessageId === idx && <MessageHoverMenu message={msg} onReply={setReplyingTo} onForward={(m) => setForwardModal({ isOpen: true, message: m })} onDelete={handleDeleteMessage} onReact={handleSendReaction} onEdit={(m) => { setEditingMessage(m); setInputText(m.body || ''); setReplyingTo(null); }} onClose={() => setActiveMenuMessageId(null)} />}
                                                <div className={`rounded-lg p-2 shadow-sm relative break-words whitespace-pre-wrap overflow-hidden [word-break:break-all] ${msg.fromMe ? 'bg-[#d9fdd3] rounded-tr-none' : 'bg-white rounded-tl-none'}`} style={{overflowWrap: 'anywhere'}}>
                                                    {msg.fromMe && msg.senderName && <p className="text-[10px] font-bold text-blue-800 mb-1 opacity-80 uppercase">{msg.senderName}</p>}
                                                    {msg.replyTo && <div className={`mb-2 p-1.5 rounded border-l-4 text-xs bg-black/5 border-gray-400 cursor-pointer hover:bg-black/10 transition-colors`} onClick={() => {
                                                        // Scroll para a mensagem original
                                                        const replyId = msg.replyTo.id;
                                                        let targetEl = null;
                                                        if (replyId) {
                                                            targetEl = document.querySelector(`[data-msg-id="${replyId}"]`);
                                                        }
                                                        if (!targetEl && msg.replyTo.body) {
                                                            // Fallback: procura por timestamp
                                                            const allMsgs = document.querySelectorAll('[data-msg-id]');
                                                            allMsgs.forEach(el => {
                                                                if (!targetEl && el.textContent?.includes(msg.replyTo.body?.substring(0, 30))) {
                                                                    targetEl = el;
                                                                }
                                                            });
                                                        }
                                                        if (targetEl) {
                                                            targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                            targetEl.style.transition = 'background-color 0.3s';
                                                            targetEl.style.backgroundColor = '#fef3c7';
                                                            setTimeout(() => { targetEl.style.backgroundColor = ''; }, 2000);
                                                        }
                                                    }}><p className="font-bold opacity-80">{msg.replyTo.fromMe ? 'Você' : selectedChat.name}</p><p className="opacity-70 truncate">{msg.replyTo.body}</p></div>}

                                                    {/* Renderiza mensagem apagada por nós (substitui conteúdo) */}
                                                    {(msg.deleted && msg.fromMe) ? <DeletedMessage fromMe={true} /> : (
                                                        <>
                                                            {/* Indicador de que o cliente apagou (mantém conteúdo) */}
                                                            {(msg.deleted || msg.body === 'Mensagem apagada') && !msg.fromMe && <DeletedIndicator />}
                                                            {msg.type === 'call' ? <CallMessage message={msg} /> : (
                                                                <>
                                                                    {/* Renderiza sticker/figurinha */}
                                                                    {msg.type === 'sticker' && msg.mediaUrl && (
                                                                        <img
                                                                            src={`${BASE_MEDIA_URL}${msg.mediaUrl}`}
                                                                            alt="Sticker"
                                                                            className="rounded-none mb-2 cursor-pointer max-w-[200px] max-h-[200px]"
                                                                            onClick={() => window.open(`${BASE_MEDIA_URL}${msg.mediaUrl}`, '_blank')}
                                                                        />
                                                                    )}

                                                                    {/* Renderiza reação */}
                                                                    {msg.type === 'reaction' && (
                                                                        <div className="flex items-center gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                                                                            <span className="text-2xl">{msg.reaction || '👍'}</span>
                                                                            <span className="text-xs text-gray-600 italic">
                                                                                Reagiu {msg.reactionTo ? `a uma mensagem` : ''}
                                                                            </span>
                                                                        </div>
                                                                    )}

                                                                    {msg.type === 'image' && msg.mediaUrl && <img src={`${BASE_MEDIA_URL}${msg.mediaUrl}`} alt="Imagem" className="rounded-lg mb-1 cursor-pointer max-w-full" onClick={() => window.open(`${BASE_MEDIA_URL}${msg.mediaUrl}`, '_blank')} />}
                                                                    {msg.type === 'image' && msg.body && msg.body !== '[Imagem]' && <p className="text-sm mt-1"><LinkifiedText text={msg.body} /></p>}
                                                                    {msg.type === 'video' && msg.mediaUrl && <div className="rounded-lg overflow-hidden mb-1 max-w-full bg-black"><video controls src={`${BASE_MEDIA_URL}${msg.mediaUrl}`} className="w-full h-auto max-h-[400px] min-w-[250px]" /></div>}
                                                                    {msg.type === 'video' && msg.body && msg.body !== '[Vídeo]' && <p className="text-sm mt-1"><LinkifiedText text={msg.body} /></p>}
                                                                    {msg.type === 'audio' && msg.mediaUrl && (() => {
                                                                        const audioFile = msg.mediaUrl.split('/').pop();
                                                                        return (
                                                                            <audio controls className="h-10 min-w-[260px] mt-1 accent-blue-600 cursor-pointer z-20 relative" preload="metadata">
                                                                                <source src={`${BASE_MEDIA_URL}/media/audio/${audioFile}`} type="audio/ogg" />
                                                                                <source src={`${BASE_MEDIA_URL}${msg.mediaUrl}`} type="audio/ogg" />
                                                                                Seu navegador não suporta reprodução de áudio.
                                                                            </audio>
                                                                        );
                                                                    })()}
                                                                    {/* Renderiza documento */}
                                                                    {msg.type === 'document' && msg.mediaUrl && <a href={`${BASE_MEDIA_URL}${msg.mediaUrl}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 bg-black/5 rounded hover:bg-black/10"><Paperclip className="w-4 h-4" /> <span className="text-sm">{msg.fileName || 'Documento'}</span></a>}
                                                                    {msg.type === 'document' && msg.body && msg.body !== '[Documento]' && <p className="text-sm mt-1"><LinkifiedText text={msg.body} /></p>}

                                                                    {/* Renderiza MENU INTERATIVO */}
                                                                    {msg.type === 'interactive' && (
                                                                        <div className="flex flex-col gap-1 min-w-[250px]">
                                                                            {msg.header && <p className="font-bold text-sm text-gray-800">{msg.header}</p>}
                                                                            <LinkifiedText text={msg.body || msg.originalBody || ''} />
                                                                            {msg.footer && <p className="text-[10px] text-gray-500 italic mt-1">{msg.footer}</p>}

                                                                            {msg.options && msg.options.length > 0 && (
                                                                                <div className="flex flex-col gap-2 mt-2 pt-2 border-t border-gray-100">
                                                                                    {msg.options.map((opt, idx) => (
                                                                                        <button
                                                                                            key={idx}
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                setInputText(opt.text);
                                                                                                // Pequeno delay para garantir que o state atualizou antes de enviar
                                                                                                setTimeout(() => {
                                                                                                    // Simula o evento de submit do form
                                                                                                    const simulatedEvent = { preventDefault: () => { } };
                                                                                                    // Como handleSendMessage usa o state 'inputText', precisamos setá-lo e chamar a função
                                                                                                    // Mas handleSendMessage limpa o input. A melhor forma é chamar a API direto ou adaptar handleSendMessage.
                                                                                                    // Vamos usar uma abordagem mais direta: chamar a API de envio aqui mesmo ou setar e clicar no enviar?
                                                                                                    // Melhor: Chamar a função de envio passando o texto explicitamente se puder, mas ela usa state.
                                                                                                    // Hack: setInputText e chamar o envio no próximo tick, mas o envio depende do state atualizado.
                                                                                                    // Vamos criar uma função auxiliar handleSendOption abaixo ou adaptar a chamada.

                                                                                                    // OPÇÃO 2: Simplesmente preencher o input e focar para o usuário enviar? 
                                                                                                    // O User pediu para "escolher uma opção... e clicamos em enviar (implícito ou explícito?) -> 'nos selecionamos um item e clicamos em enviar'" 
                                                                                                    // Parece que ele quer que o CLIQUE envie.

                                                                                                    // Vamos fazer o botão chamar o envio diretamente.
                                                                                                    // Precisamos ter acesso à função de envio. O handleSendMessage usa scope local.
                                                                                                    // Vamos refatorar handleSendMessage para aceitar texto opcional ou criar um handler aqui.
                                                                                                    // Como não posso refatorar o handleSendMessage facilmente sem ver ele todo, vou replicar a lógica de envio simplificada aqui.
                                                                                                    api.post(`/chats/${selectedChat.id}/messages`, { body: opt.text, replyTo: null })
                                                                                                        .then(res => {
                                                                                                            const newMsg = { ...res.data, status: 'sent', fromMe: true, senderName: currentUser.name };
                                                                                                            setMessages(prev => [...prev, newMsg]);
                                                                                                        })
                                                                                                        .catch(() => alert('Erro ao enviar opção.'));
                                                                                                }, 50);
                                                                                            }}
                                                                                            className="p-2 bg-blue-50 hover:bg-blue-100 text-blue-700 text-sm font-medium rounded-lg text-center transition-colors border border-blue-100 uppercase"
                                                                                        >
                                                                                            {opt.text}
                                                                                        </button>
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}

                                                                    {/* Renderiza corpo da mensagem para tipos que não têm mídia */}
                                                                    {!['contact', 'sticker', 'reaction', 'call', 'audio', 'image', 'video', 'document', 'interactive'].includes(msg.type) && msg.body && <LinkifiedText text={msg.body} />}

                                                                    {/* Fallback para tipos não suportados */}
                                                                    {!['image', 'video', 'audio', 'document', 'sticker', 'reaction', 'call', 'text'].includes(msg.type) && !msg.body && !msg.mediaUrl && (
                                                                        <span className="text-xs text-gray-500 italic">Mensagem não suportada ({msg.type || 'desconhecido'})</span>
                                                                    )}
                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                    <div className="flex items-center justify-end gap-1 mt-1 text-[10px] text-gray-500">
                                                        {msg.isEdited && <span className="italic text-gray-400">✏️ editada</span>}
                                                        <span>{msg.timestamp && !isNaN(new Date(msg.timestamp).getTime()) ? new Date(msg.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                                                        <MessageReadStatus message={msg} chatType={selectedChat.isGroup ? 'group' : 'individual'} />
                                                    </div>
                                                </div>
                                                {/* Reação — fora do overflow-hidden para não ser cortada */}
                                                <MessageReaction reaction={msg.reaction} fromMe={msg.fromMe} />
                                            </div>
                                        </div>
                                    );
                                })}
                            <div ref={messagesEndRef} />
                        </div>

                        {replyingTo && (<div className="p-2 bg-gray-100 border-l-4 border-blue-500 flex justify-between items-center mx-2 mt-2 rounded-r shrink-0"><div className="text-sm"><span className="font-bold text-blue-600">Respondendo a {replyingTo.fromMe ? 'Você' : selectedChat.name}</span><p className="text-gray-600 truncate text-xs">{replyingTo.body}</p></div><button onClick={() => setReplyingTo(null)}><X className="w-4 h-4" /></button></div>)}

                        {/* ✏️ Barra de edição */}
                        {editingMessage && (<div className="p-2 bg-green-50 border-l-4 border-green-500 flex justify-between items-center mx-2 mt-2 rounded-r shrink-0"><div className="text-sm flex items-center gap-2"><Pencil className="w-4 h-4 text-green-600" /><div><span className="font-bold text-green-700">Editando mensagem</span><p className="text-gray-600 truncate text-xs max-w-[300px]">{editingMessage.body}</p></div></div><button onClick={() => { setEditingMessage(null); setInputText(''); }}><X className="w-4 h-4" /></button></div>)}

                        {
                            (selectedChat.status !== 'ended' && selectedChat.status !== 'finished' && selectedChat.status !== 'canceled') && (
                                <div className="p-2 bg-white border-t flex items-center gap-2 shrink-0 pb-safe w-full max-w-[100vw] overflow-hidden">
                                    {isRecording ? (
                                        <div className="flex-1 flex items-center gap-3 bg-red-50 px-4 py-2 rounded-xl animate-pulse">
                                            <div className="w-3 h-3 bg-red-600 rounded-full" />
                                            <span className="text-red-600 font-bold">{formatTime(recordingTime)}</span>
                                        </div>
                                    ) : recordedAudio ? (
                                        <div className="flex-1 flex items-center bg-gray-100 rounded-xl p-2 gap-2">
                                            <audio controls src={recordedAudio.url} className="flex-1 h-8" />
                                            <button onClick={() => setRecordedAudio(null)} className="text-red-500 p-2"><Trash2 className="w-5 h-5" /></button>
                                            <button onClick={handleSendAudio} className="text-green-500 p-2"><Send className="w-5 h-5" /></button>
                                        </div>
                                    ) : (
                                        <>
                                            <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileUpload} />
                                            <button onClick={() => fileInputRef.current.click()} className="p-2 text-gray-500 hover:bg-gray-100 rounded-full shrink-0"><Paperclip className="w-6 h-6" /></button>
                                            <button onClick={() => setIsQuickMsgModalOpen(true)} className="p-2 text-gray-500 hover:bg-gray-100 rounded-full hidden md:block shrink-0"><Zap className="w-6 h-6" /></button>
                                            <div className="flex-1 min-w-0 bg-white rounded-xl flex items-center px-3 py-2 border border-gray-200 focus-within:border-blue-400 shadow-sm">
                                                <input className="w-full bg-transparent outline-none text-gray-700 min-h-[24px]" placeholder="Digite..." value={inputText} onChange={(e) => setInputText(e.target.value)} onPaste={handlePaste} onKeyDown={(e) => e.key === 'Enter' && handleSendMessage(e)} />
                                            </div>
                                        </>
                                    )}
                                    {(inputText.trim()) ? (
                                        <button onClick={handleSendMessage} disabled={isSending} className={`p-3 text-white rounded-full shadow-md transition-colors shrink-0 ${isSending ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}>{isSending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}</button>
                                    ) : (!recordedAudio) ? (
                                        <button onClick={isRecording ? stopRecording : startRecording} className={`p-3 rounded-full shrink-0 ${isRecording ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-600'}`}>{isRecording ? <Square className="w-5 h-5 fill-current" /> : <Mic className="w-5 h-5" />}</button>
                                    ) : null}
                                </div>
                            )
                        }
                    </>
                )}
            </div>

            {viewImage && <div className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center backdrop-blur-sm" onClick={() => setViewImage(null)}><img src={viewImage} className="max-h-[90%] max-w-[90%] rounded-lg" alt="" /><button className="absolute top-6 right-6 text-white bg-gray-800 p-2 rounded-full"><X className="w-6 h-6" /></button></div>}

            {/* MODAIS */}
            {isClientModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-60 z-[70] flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-6 border-t-4 border-orange-500 animate-in zoom-in-95">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold flex items-center text-gray-800"><User className="w-6 h-6 mr-2 text-orange-500" />{pendingAction ? 'Atualizar Cadastro (Obrigatório)' : 'Editar Cliente'}</h2>
                            {pendingAction && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded font-bold uppercase tracking-wide">Obrigatório</span>}
                        </div>
                        <p className="text-sm text-gray-600 mb-6 leading-relaxed">{pendingAction ? "A empresa exige que os dados do cliente estejam completos antes de prosseguir com esta ação." : "Edite os dados do cliente abaixo."}<br /><br />Por favor, informe o <b>Nome</b> e o <b>Telefone Real</b> (55...).</p>
                        <form onSubmit={handleSaveClientData} className="space-y-4">
                            <div><label className="block text-sm font-bold text-gray-700 mb-1">Nome Completo</label><input type="text" required className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all" value={clientFormData.name} onChange={e => setClientFormData({ ...clientFormData, name: e.target.value })} placeholder="Ex: João Silva" /></div>
                            <div><label className="block text-sm font-bold text-gray-700 mb-1">Telefone (WhatsApp)</label><input type="text" required placeholder="Ex: 5511999999999" className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all" value={clientFormData.phone} onChange={e => setClientFormData({ ...clientFormData, phone: e.target.value })} />
                                {clientFormData.lidDisplay && clientFormData.lidDisplay.includes('@lid') && (<div className="mt-2 bg-blue-50 border border-blue-100 rounded p-2 flex items-start gap-2"><AlertTriangle className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" /><p className="text-xs text-blue-700"><b>Vínculo Interno (LID):</b> {clientFormData.lidDisplay}<br />O sistema irá vincular este ID ao número que você digitar acima.</p></div>)}
                            </div>
                            <div><label className="block text-sm font-bold text-gray-700 mb-1">Empresa (Opcional)</label><input type="text" className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all" value={clientFormData.company} onChange={e => setClientFormData({ ...clientFormData, company: e.target.value })} placeholder="Ex: ACME Ltda" /></div>
                            <div className="flex justify-end space-x-3 mt-8 pt-4 border-t"><button type="button" onClick={() => { setIsClientModalOpen(false); setPendingAction(null); }} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium">Cancelar</button><button type="submit" className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center font-bold shadow-md transition-colors"><Save className="w-4 h-4 mr-2" />Salvar e Continuar</button></div>
                        </form>
                    </div>
                </div>
            )}

            <Modal isOpen={isStartChatModalOpen} onClose={() => { setIsStartChatModalOpen(false); setClientSearchTerm(''); }} title="Nova Conversa" confirmText="Iniciar" onConfirm={handleStartConversation}>
                <div className="space-y-4">
                    {/* BUSCA INTELIGENTE DE CLIENTE */}
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Buscar Cliente</label>
                        <div className="relative">
                            <input
                                type="text"
                                className="w-full p-2 pl-8 border rounded-lg bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                placeholder="Nome ou telefone..."
                                value={clientSearchTerm}
                                onChange={(e) => setClientSearchTerm(e.target.value)}
                            />
                            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
                        </div>
                    </div>

                    {/* LISTA DE RESULTADOS */}
                    <div className="max-h-40 overflow-y-auto border rounded-lg bg-white custom-scrollbar">
                        {clientsList.filter(c =>
                            c.name?.toLowerCase().includes(clientSearchTerm.toLowerCase()) ||
                            c.phone?.includes(clientSearchTerm)
                        ).length === 0 ? (
                            <p className="p-3 text-center text-gray-400 text-sm">Nenhum cliente encontrado.</p>
                        ) : (
                            clientsList
                                .filter(c =>
                                    !clientSearchTerm ||
                                    c.name?.toLowerCase().includes(clientSearchTerm.toLowerCase()) ||
                                    c.phone?.includes(clientSearchTerm)
                                )
                                .slice(0, 50) // Limita a 50 resultados para performance
                                .map(c => (
                                    <div
                                        key={c.id || c._id}
                                        onClick={() => setNewChatClient(c.id || c._id)}
                                        className={`p-2 border-b last:border-0 cursor-pointer flex items-center justify-between hover:bg-blue-50 transition-colors ${newChatClient === (c.id || c._id) ? 'bg-blue-100 border-l-4 border-l-blue-600' : ''}`}
                                    >
                                        <div>
                                            <p className="font-bold text-sm text-gray-800">{c.name}</p>
                                            <p className="text-xs text-gray-500">{cleanPhone(c.phone)}</p>
                                        </div>
                                        {newChatClient === (c.id || c._id) && <CheckCircle className="w-5 h-5 text-blue-600" />}
                                    </div>
                                ))
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Mensagem Inicial</label>
                        <textarea className="w-full p-2 border rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none" rows="3" value={newChatMessage} onChange={(e) => setNewChatMessage(e.target.value)} placeholder="Digite a mensagem inicial..." />
                    </div>
                </div>
            </Modal>
            <Modal isOpen={addAttendantModal.isOpen} onClose={() => setAddAttendantModal({ isOpen: false })} title="Adicionar Atendente" confirmText="Adicionar" onConfirm={handleAddAttendant}>
                <div className="space-y-2">{(usersList || []).filter(u => u._id !== currentUser._id).map(user => (<label key={user._id} className="flex items-center p-2 border rounded hover:bg-gray-50 cursor-pointer"><input type="checkbox" checked={selectedAttendants.includes(user._id)} onChange={(e) => { if (e.target.checked) setSelectedAttendants([...selectedAttendants, user._id]); else setSelectedAttendants(selectedAttendants.filter(id => id !== user._id)); }} className="mr-3" /><span>{user.name}</span></label>))}</div>
            </Modal>
            <Modal isOpen={osModal.isOpen} onClose={() => setOsModal({ isOpen: false, data: null })} title="Gerar O.S." confirmText="Criar" onConfirm={handleSaveOs}>
                {osModal.data && (<div className="space-y-3"><input type="text" value={osModal.data.clientName} onChange={(e) => setOsModal({ ...osModal, data: { ...osModal.data, clientName: e.target.value } })} className="w-full p-2 border rounded" placeholder="Cliente" /><textarea value={osModal.data.requestDescription} onChange={(e) => setOsModal({ ...osModal, data: { ...osModal.data, requestDescription: e.target.value } })} className="w-full p-2 border rounded" rows="4" placeholder="Descrição..." /></div>)}
            </Modal>
            <Modal isOpen={isFinishModalOpen} onClose={() => setIsFinishModalOpen(false)} title="Finalizar" confirmText="Sim, Finalizar" onConfirm={() => handleStatusChange('finish')}>
                <div className="text-center p-4"><CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" /><p className="text-gray-600">Deseja finalizar este atendimento?</p></div>
            </Modal>
            <Modal isOpen={isTransferModalOpen} onClose={() => setIsTransferModalOpen(false)} title="Transferir" confirmText="Transferir" onConfirm={handleTransfer}>
                <select className="w-full p-3 border rounded bg-white" value={transferTarget} onChange={(e) => setTransferTarget(e.target.value)}>
                    <option value="">Selecione o Agente...</option>
                    {(usersList || []).map(u => <option key={u._id} value={u._id}>{u.name} {u.online ? '🟢' : '⚪'}</option>)}
                </select>
            </Modal>
            <Modal isOpen={isQuickMsgModalOpen} onClose={() => setIsQuickMsgModalOpen(false)} title="Mensagens Rápidas">
                <div className="max-h-80 overflow-y-auto space-y-2">{(quickMessages || []).map(qm => (<div key={qm._id} className="p-3 border rounded hover:bg-blue-50 cursor-pointer" onClick={() => { setInputText(prev => prev + qm.text); setIsQuickMsgModalOpen(false); }}><h4 className="font-bold text-blue-700 text-sm">{qm.title}</h4><p className="text-xs text-gray-600 truncate">{qm.text}</p></div>))}</div>
            </Modal>
            <Modal isOpen={forwardModal.isOpen} onClose={() => setForwardModal({ isOpen: false, message: null })} title="Encaminhar" confirmText="Enviar" onConfirm={() => { const selected = document.querySelector('input[name="forward-chat"]:checked'); if (selected) handleForwardMessage(selected.value); }}>
                <div className="space-y-2 max-h-96 overflow-y-auto">{(chats || []).map(chat => (<label key={chat.id} className="flex items-center p-2 border rounded hover:bg-gray-50 cursor-pointer"><input type="radio" name="forward-chat" value={chat.id} className="mr-3" /><div className="flex-1"><p className="font-bold text-sm">{chat.name}</p><p className="text-xs text-gray-500">{cleanPhone(chat.contact_id)}</p></div></label>))}</div>
            </Modal>
            <Modal 
                isOpen={actionModal.isOpen} 
                onClose={() => { setActionModal({ isOpen: false, type: null }); setPauseReason(''); }} 
                title={actionModal.type === 'pause' ? "Pausar Atendimento" : "Confirmação"} 
                confirmText={actionModal.type === 'pause' ? "Confirmar Pausa" : "Sim"} 
                onConfirm={() => {
                    if (actionModal.type === 'pause' && !pauseReason.trim()) {
                        alert('Por favor, informe o motivo da pausa.');
                        return;
                    }
                    handleStatusChange(actionModal.type, actionModal.type === 'pause' ? { pauseReason } : {});
                }}
            >
                {actionModal.type === 'pause' ? (
                    <div className="space-y-4">
                        <p className="text-gray-600 text-sm">Por favor, informe o motivo pelo qual este atendimento está sendo pausado. O cliente será questionado sobre este motivo ao retornar.</p>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Motivo da Pausa *</label>
                            <textarea
                                value={pauseReason}
                                onChange={(e) => setPauseReason(e.target.value)}
                                className="w-full p-3 border rounded-lg text-sm bg-gray-50 focus:bg-white transition-colors min-h-[100px]"
                                placeholder="Ex: Aguardando envio de documento do cliente..."
                                required
                            />
                        </div>
                    </div>
                ) : (
                    <p>Confirma esta ação?</p>
                )}
            </Modal>

            {/* MODAL DE HISTÓRICO (LISTA) */}
            <Modal isOpen={historyModal.isOpen} onClose={() => setHistoryModal({ isOpen: false, data: [] })} title="Histórico de Atendimentos">
                <div className="max-h-96 overflow-y-auto space-y-2">
                    {historyModal.data.length === 0 ? (
                        <p className="text-gray-500 text-center py-4">Nenhum atendimento encontrado</p>
                    ) : (
                        historyModal.data.map((ticket) => (
                            <div key={ticket._id} className="p-3 border rounded hover:bg-gray-50 transition-colors flex flex-col gap-2">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <span className="font-bold text-sm block">Protocolo: {ticket.protocol}</span>
                                        <p className="text-xs text-gray-500">
                                            {new Date(ticket.createdAt).toLocaleString('pt-BR')}
                                        </p>
                                    </div>
                                    {getStatusBadge(ticket.status)}
                                </div>

                                {ticket.agentId && (
                                    <p className="text-xs text-gray-600">
                                        Atendente: <span className="font-semibold">{ticket.agentId.name}</span>
                                    </p>
                                )}

                                <div className="flex items-end justify-between mt-1">
                                    <p className="text-xs text-gray-500 truncate max-w-[70%] italic">
                                        {ticket.messages && ticket.messages.length > 0
                                            ? (ticket.messages[ticket.messages.length - 1].body || '[Mídia]')
                                            : 'Sem mensagens'}
                                    </p>
                                    <button
                                        onClick={() => handleOpenHistoryDetails(ticket)}
                                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-bold hover:bg-blue-100 transition-colors"
                                    >
                                        <Eye className="w-3 h-3" /> Visualizar
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </Modal>

            {/* MODAL DE DETALHES DO HISTÓRICO (VISUALIZAÇÃO) */}
            <Modal
                isOpen={historyDetails.isOpen}
                onClose={() => setHistoryDetails({ isOpen: false, ticket: null, messages: [] })}
                title={`Visualizando: ${historyDetails.ticket?.protocol || 'Atendimento'}`}
                maxWidth="max-w-4xl w-full"
            >
                <div className="flex flex-col h-[70vh]">
                    <div className="flex-1 bg-[#e5ddd5] border rounded-lg overflow-hidden relative shadow-inner flex flex-col">
                        <div className="absolute inset-0 opacity-10 bg-[url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')] bg-repeat pointer-events-none"></div>
                        <div className="flex-1 overflow-y-auto p-4 custom-scrollbar relative z-10 space-y-2">
                            {loadingDetails ? (
                                <div className="flex justify-center items-center h-full"><Loader2 className="w-8 h-8 animate-spin text-gray-500" /></div>
                            ) : historyDetails.messages.length === 0 ? (
                                <p className="text-center text-gray-400 mt-10">Nenhuma mensagem neste atendimento.</p>
                            ) : (
                                historyDetails.messages.map((msg, idx) => renderMessageBubble(msg, idx))
                            )}
                        </div>
                    </div>
                </div>
            </Modal>

            <ClientFormModal
                isOpen={clientFormModal.isOpen}
                onClose={() => setClientFormModal({ isOpen: false, clientId: null })}
                onSuccess={handleClientFormSuccess}
                initialPhone={clientFormModal.phone || ''}
                initialName={clientFormModal.name || ''}
                initialCompany={clientFormModal.company || ''}
                initialEmail={clientFormModal.email || ''}
                initialNotes={clientFormModal.notes || ''}
                initialIgnoreMetrics={clientFormModal.ignoreMetrics || false}
                initialUsePreferredAgent={clientFormModal.usePreferredAgent || false}
                initialPreferredAgentId={clientFormModal.preferredAgentId || ''}
                initialLid={clientFormModal.lid || ''}
                clientId={clientFormModal.clientId}
            />

            {showImageEditor && imageToEdit && <ImageEditor image={imageToEdit} onSave={handleSaveEditedImage} onCancel={() => { setShowImageEditor(false); setImageToEdit(null); }} />}

            {/* 🆕 MODAL DE CATEGORIZAÇÃO */}
            {categoryModal.isOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
                        <div className="p-6 border-b">
                            <h2 className="text-lg font-bold text-gray-800">
                                {categoryModal.actionType === 'finish' ? '✅ Finalizar' : categoryModal.actionType === 'cancel' ? '❌ Cancelar' : categoryModal.actionType === 'pause' ? '⏸️ Pausar' : '📤 Externo'} Atendimento
                            </h2>
                            <p className="text-sm text-gray-500 mt-1">Selecione a categoria para registrar este atendimento.</p>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="text-sm font-bold text-gray-700 block mb-1">Categoria *</label>
                                <select
                                    value={selectedCategoryId}
                                    onChange={e => { setSelectedCategoryId(e.target.value); setSelectedSubcategoryId(''); }}
                                    className="w-full border rounded-lg p-2.5 bg-white text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                >
                                    <option value="">-- Selecione uma categoria --</option>
                                    {categoriesList.map(cat => (
                                        <option key={cat._id} value={cat._id}>{cat.name}</option>
                                    ))}
                                </select>
                            </div>
                            {selectedCategoryId && (() => {
                                const cat = categoriesList.find(c => c._id === selectedCategoryId);
                                return cat?.subcategories?.length > 0 ? (
                                    <div>
                                        <label className="text-sm font-bold text-gray-700 block mb-1">Subcategoria</label>
                                        <select
                                            value={selectedSubcategoryId}
                                            onChange={e => setSelectedSubcategoryId(e.target.value)}
                                            className="w-full border rounded-lg p-2.5 bg-white text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                        >
                                            <option value="">-- Selecione (opcional) --</option>
                                            {cat.subcategories.map((sub, idx) => (
                                                <option key={idx} value={String(idx)}>{sub.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                ) : null;
                            })()}
                            
                            {/* 🆕 PAUSE REASON NA CATEGORIZAÇÃO */}
                            {categoryModal.actionType === 'pause' && (
                                <div className="mt-4">
                                    <label className="text-sm font-bold text-gray-700 block mb-1">Motivo da Pausa *</label>
                                    <textarea
                                        value={pauseReason}
                                        onChange={(e) => setPauseReason(e.target.value)}
                                        className="w-full p-3 border rounded-lg text-sm bg-gray-50 focus:bg-white transition-colors min-h-[80px]"
                                        placeholder="Ex: Aguardando envio de documento do cliente..."
                                        required
                                    />
                                </div>
                            )}
                        </div>
                        <div className="p-4 border-t flex gap-3 justify-end">
                            <button
                                onClick={() => setCategoryModal({ isOpen: false, actionType: null })}
                                className="px-4 py-2 rounded-lg border text-gray-600 hover:bg-gray-50 text-sm font-medium"
                            >
                                Cancelar
                            </button>
                            <button
                                disabled={!selectedCategoryId || isGlobalLoading}
                                onClick={async () => {
                                    const cat = categoriesList.find(c => c._id === selectedCategoryId);
                                    const sub = selectedSubcategoryId !== '' ? cat?.subcategories?.[Number(selectedSubcategoryId)] : null;
                                    const action = categoryModal.actionType;
                                    
                                    if (action === 'pause' && !pauseReason.trim()) {
                                        alert('Por favor, informe o motivo da pausa.');
                                        return;
                                    }

                                    setCategoryModal({ isOpen: false, actionType: null });
                                    await handleStatusChange(action, {
                                        categoryId: selectedCategoryId,
                                        categoryName: cat?.name,
                                        subcategoryId: sub ? sub._id : undefined,
                                        subcategoryName: sub ? sub.name : undefined,
                                        ...(action === 'pause' ? { pauseReason } : {})
                                    });
                                }}
                                className={`px-4 py-2 rounded-lg text-sm font-bold text-white transition-colors ${categoryModal.actionType === 'finish'
                                    ? 'bg-green-600 hover:bg-green-700 disabled:bg-green-300'
                                    : categoryModal.actionType === 'cancel'
                                    ? 'bg-red-600 hover:bg-red-700 disabled:bg-red-300'
                                    : 'bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300'
                                    } disabled:cursor-not-allowed`}
                            >
                                {isGlobalLoading ? 'Aguarde...' : categoryModal.actionType === 'finish' ? 'Confirmar Finalização' : categoryModal.actionType === 'cancel' ? 'Confirmar Cancelamento' : categoryModal.actionType === 'pause' ? 'Confirmar Pausa' : 'Confirmar Transferência'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChatScreen;